// Riverside Vet · Cert IV ring-in triage trainer — rule-based scenario library + scoring.
// 100% client-side. No persistence, no network. Scenarios are served at random.
//
// Each scenario plays as a 4-decision call:
//   Communication (opening) -> History (question) -> Advice -> Urgency (triage category)
// While the caller speaks, the operator taps "red-flag" chips before they lock.
//
// Scoring (Cert IV rubric, 100 pts):
//   Decision-tree responses (communication/history/advice) ... 40
//   Red-Flag Radar (live tapping) ............................ 25
//   Final triage category .................................... 25
//   Client safety & professional communication .............. 10

export const TRIAGE_SCENARIOS = [
  {
    id: "lily-cat",
    caller: "Ms Carter",
    patient: "Luna",
    signalment: "DSH, 2y FN, ~4 kg",
    demeanour: "Casual — doesn't realise it's serious",
    summary: "Owner's bouquet; cat chewed the leaves about an hour ago.",
    coachTitle: "Lily exposure — cat (nephrotoxic emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller:
          "Hi, sorry to bother you — my cat chewed some leaves off the flowers in a bouquet. Probably nothing, but I thought I'd check.",
        chips: [
          { t: "chewed some leaves off the flowers", flag: true, coach: "Plant ingestion in a cat is an immediate red flag — bouquet flowers are a classic lily exposure." },
          { t: "Probably nothing", flag: false },
        ],
        options: [
          { t: "You did the right thing calling. Can you tell me exactly what kind of flower it was?", score: 3, good: "Calm, validating opening that immediately gathers the critical detail — the plant species." },
          { t: "Honestly, cats nibble on plants and flowers all the time and usually bring a bit back up — I really wouldn't lose any sleep over a few chewed leaves.", score: 0, harmful: true, bad: "Falsely reassuring — this dismisses a potentially lethal exposure before any history is taken." },
          { t: "Why don't you just keep a close eye on her at home for the rest of the day, and only ring us back if she actually starts vomiting or seems unsettled?", score: 1, bad: "Jumps to advice before identifying the plant — premature for a possible toxin." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller:
          "The florist said it was a mix — there are some big white lilies in it. She got the lily leaves about an hour ago.",
        chips: [
          { t: "big white lilies", flag: true, coach: "Lilies (Lilium/Hemerocallis) cause acute kidney injury in cats — even leaves, pollen or vase water are toxic." },
          { t: "about an hour ago", flag: true, coach: "Recent ingestion (<2 h) means decontamination can still help — this is time-critical." },
        ],
        options: [
          { t: "Do you still have the plant so the vet can confirm it's a true lily, and has she vomited or drooled?", score: 3, good: "Targets confirmation of the toxin and any early signs — exactly what the vet needs." },
          { t: "While I've got you on the phone, can I just check whether she's fully up to date with all of her vaccinations and her flea and worming treatments?", score: 1, bad: "Reasonable housekeeping, but secondary to confirming the lily and the timeline." },
          { t: "Let's just keep her quiet and comfortable at home overnight and see how she goes, and we can always reassess things properly in the morning if needed.", score: 0, harmful: true, bad: "Waiting wastes the decontamination window for a lethal nephrotoxin." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "She seems totally normal — playing as usual. Are you sure I need to worry?",
        chips: [],
        options: [
          { t: "Even though she looks well, lily exposure is a true emergency for cats. Bring her in now and bring the lily — don't try to make her vomit at home.", score: 3, good: "Correct, urgent advice — see now, bring the plant, no home decontamination." },
          { t: "Try offering her a little milk to settle her stomach and watch for any vomiting, then give us a ring back only if she goes off her food over the next day or so.", score: 0, harmful: true, bad: "Home remedies and 'wait for signs' are dangerous — kidney injury is silent until advanced." },
          { t: "There's no great rush with this one, so just pop her in at some point today whenever it happens to suit you and fits around your day.", score: 1, bad: "Right destination, but the casual timing under-states a genuine emergency." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Lily ingestion in a cat is a time-critical nephrotoxic emergency — it should be triaged 'see now', not deferred.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Lily ingestion in a cat is a time-critical nephrotoxic emergency." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "puppy-vacc",
    caller: "Mrs Quinn",
    patient: "Biscuit",
    signalment: "Mini Schnauzer pup, 8 weeks, F entire, ~2 kg",
    demeanour: "Excited new owner",
    summary: "New owner booking first vaccination; pup is well & playful.",
    coachTitle: "First-vaccination booking — well puppy (routine)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Hi! We just got our new puppy Biscuit. I'd like to book her in for her shots.",
        chips: [{ t: "new puppy", flag: false }, { t: "book her in for her shots", flag: false }],
        options: [
          { t: "Congratulations! Riverside Vet, Sam speaking — happy to set that up. Can I get her age, breed and current weight?", score: 3, good: "Warm, professional opening that gathers signalment up front." },
          { t: "We're pretty heavily booked at the moment, so we can probably look at fitting her in sometime early next year once the diary quietens down a bit.", score: 0, harmful: true, bad: "Inappropriate delay — primary puppy vaccinations are time-sensitive." },
          { t: "Honestly, you don't really need to bother with vaccines at all if she's going to be an indoor dog who won't be mixing with other animals.", score: 0, harmful: true, bad: "Clinically wrong and unsafe advice." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She's a Mini Schnauzer, just turned 8 weeks, about 2 kilos. The breeder said she's had her first injection.",
        chips: [{ t: "had her first injection", flag: false }],
        options: [
          { t: "Could you bring the breeder's paperwork showing what she had and when? It helps us plan her next dose.", score: 3, good: "Confirms prior cover so the course is planned correctly." },
          { t: "Don't worry about whatever the breeder may have given her — we'll just play it safe and start the entire vaccination course again from scratch.", score: 1, bad: "Restarting unnecessarily ignores the breeder's record — confirm first." },
          { t: "Before we book the appointment in, has she shown any signs of vomiting, diarrhoea, coughing or being off her food since you first brought her home?", score: 2, bad: "A sensible wellness check, though the paperwork is the priority for a booking." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Great. Anything I should do before we come in?",
        chips: [],
        options: [
          { t: "Bring her vaccination card, keep her away from unvaccinated dogs until she's fully covered, and we'll do a full health check at the visit.", score: 3, good: "Accurate routine advice — paperwork, biosecurity and a health check." },
          { t: "Take her down to the local dog park a few times beforehand and let her socialise freely, so she's nicely used to being around other dogs by then.", score: 0, harmful: true, bad: "Unsafe — an incompletely vaccinated pup shouldn't mix with unknown dogs." },
          { t: "There's really nothing in particular you need to do or prepare beforehand — just turn up on the day with her and we'll take care of everything from there.", score: 1, bad: "Misses the chance to give useful pre-visit and biosecurity advice." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A well puppy booking a routine vaccination is a 'book in' — over-triaging ties up emergency capacity.",
      options: [
        { t: "Routine — book in", score: 25, why: "A well, playful puppy for a scheduled first vaccination is a routine booking." },
        { t: "Urgent — today", score: 6 },
        { t: "Emergency — see now", score: 0 },
        { t: "Advice & monitor at home", score: 10 },
      ],
    },
  },

  {
    id: "fb-vomiting-dog",
    caller: "Mrs Tate",
    patient: "Murphy",
    signalment: "Golden Retriever, 9y MN, ~33 kg",
    demeanour: "Calm, methodical",
    summary: "Vomiting since this morning; ate part of a chew toy yesterday.",
    coachTitle: "Vomiting + foreign body risk — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Hi, Murphy's been vomiting all morning. He's 9, a Golden Retriever.",
        chips: [{ t: "vomiting all morning", flag: true, coach: "Repeated vomiting in an older large-breed dog warrants same-day assessment." }],
        options: [
          { t: "Riverside Vet, Sam speaking — I'm sorry to hear that. How many times has he vomited, and is anything unusual coming up?", score: 3, good: "Empathetic opening that immediately quantifies the vomiting." },
          { t: "In my experience most dogs that have a bit of a vomit are absolutely fine again by the afternoon once it's worked through, so do try not to panic too much.", score: 0, harmful: true, bad: "Dismissive — anchors on a benign outcome before any history." },
          { t: "Before anything else, can you think of anything unusual he might have eaten or got hold of that he really shouldn't have over the last day or two?", score: 2, bad: "A good question, but lead with empathy and quantify the vomiting first." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's vomited about eight times — mostly clear fluid now. He swallowed part of a chew toy yesterday.",
        chips: [
          { t: "about eight times", flag: true, coach: "Eight episodes in one morning is high-frequency vomiting — risk of dehydration and obstruction." },
          { t: "swallowed part of a chew toy", flag: true, coach: "A swallowed foreign body with vomiting is a classic GI obstruction picture — surgical until proven otherwise." },
        ],
        options: [
          { t: "Is he able to keep any water down, and is his belly swollen or painful?", score: 3, good: "Checks hydration and screens for obstruction/abdominal pain." },
          { t: "Out of interest, what brand of chew toy was it that he had — some of them are made of much tougher, harder-wearing material than others, aren't they?", score: 1, bad: "The brand is irrelevant — focus on whether the object is obstructing." },
          { t: "Why not try withholding all of his food for a full twenty-four hours to rest his stomach, and just see whether the vomiting settles down on its own?", score: 0, harmful: true, bad: "Watchful waiting is dangerous with a possible obstruction." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "He won't drink — he just brings it straight back up. His belly doesn't look swollen but he hates me touching it.",
        chips: [
          { t: "won't drink", flag: false },
          { t: "hates me touching it", flag: true, coach: "Abdominal pain on handling supports obstruction and raises the urgency." },
        ],
        options: [
          { t: "Given the chew toy and the pattern, we need to see him within the next couple of hours. Nil by mouth from now — no food or water — and bring the missing piece of toy.", score: 3, good: "Correct: see now, NBM, bring the object for sizing." },
          { t: "Offer him small, frequent sips of water through the afternoon and a light, bland chicken-and-rice meal tonight to ease his stomach back into eating.", score: 0, harmful: true, bad: "Feeding a possibly obstructed, vomiting dog is unsafe." },
          { t: "Let's get him booked in for a general check-up with one of the vets at some point later this week, once we've had a chance to see how he settles.", score: 0, harmful: true, bad: "Far too slow for a likely obstruction." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Persistent vomiting with a swallowed foreign body and abdominal pain is an emergency presentation.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Repeated vomiting, a swallowed object and abdominal pain point to obstruction — see now." },
        { t: "Urgent — today", score: 14 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "seizure-dog",
    caller: "Mr Hughes",
    patient: "Rocco",
    signalment: "Staffy, 4y MN, ~22 kg",
    demeanour: "Panicked",
    summary: "Has been fitting for several minutes; owner panicked.",
    coachTitle: "Active seizure / status epilepticus — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog is having a fit! He's never done this before — what do I do?!",
        chips: [
          { t: "having a fit", flag: true, coach: "An active seizure is an emergency — prolonged fitting risks brain injury and hyperthermia." },
          { t: "never done this before", flag: true, coach: "A first-ever seizure has no known cause and must be assessed urgently." },
        ],
        options: [
          { t: "I'm Sam at Riverside Vet — I can help. First, please keep your hands away from his mouth. How long has he been fitting?", score: 3, good: "Calms the owner, gives a safety instruction, and times the seizure." },
          { t: "Quickly put your fingers or something flat into his mouth so that he can't accidentally swallow his own tongue while he's fitting like that.", score: 0, harmful: true, bad: "Dangerous myth — risks a serious bite and helps nothing." },
          { t: "If you've got any sedatives or first-aid medication of your own in the house, give him a little of that to try and bring the fit to a stop.", score: 0, harmful: true, bad: "Never advise human medication — many are toxic to dogs." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's been fitting for about five minutes — he's still going!",
        chips: [{ t: "five minutes — he's still going", flag: true, coach: "A seizure lasting >5 minutes is status epilepticus — immediately life-threatening." }],
        options: [
          { t: "Is he still seizing right now, and is there anything nearby he could hurt himself on?", score: 3, good: "Confirms ongoing status and makes the environment safe." },
          { t: "While we work out what's going on, can I just confirm whether he's had any vaccinations or started any new medications in the last week or two?", score: 1, bad: "Not the priority while he is actively seizing." },
          { t: "Could you take a quick video of the seizure on your phone and send it through to us first, so the vet can have a proper look before you set off?", score: 0, harmful: true, bad: "Delays care during status epilepticus." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Okay — what should I do right now?",
        chips: [],
        options: [
          { t: "Move furniture away so he can't hurt himself, time it on your phone, don't restrain him, and bring him in the moment he stops — I'm alerting the vet now.", score: 3, good: "Textbook seizure first aid plus a warning call ahead." },
          { t: "Just hold him nice and still and give him a reassuring cuddle on your lap until the fit passes and he gradually comes back round again.", score: 0, harmful: true, bad: "Restraining a seizing dog risks injury to both of you." },
          { t: "Let's wait and see how he is by tomorrow, and only think about bringing him in if he goes on to have another fit like that one again.", score: 0, harmful: true, bad: "Ongoing status epilepticus cannot wait." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A seizure still ongoing past five minutes is status epilepticus — an immediate 'see now' emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Ongoing seizure beyond five minutes (status epilepticus) is immediately life-threatening." },
        { t: "Urgent — today", score: 6 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "pyometra",
    caller: "Mrs Doyle",
    patient: "Pearl",
    signalment: "Staffy, 7y F entire, ~24 kg",
    demeanour: "Chatty, a bit dismissive",
    summary: "Off colour for two days; some discharge; drinking a lot.",
    coachTitle: "Suspected pyometra — entire bitch (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Pearl's just been a bit flat the last couple of days. She's seven, a Staffy, not desexed.",
        chips: [{ t: "not desexed", flag: true, coach: "An entire older bitch who is unwell is a pyometra until proven otherwise." }],
        options: [
          { t: "Riverside Vet, Sam speaking. Thanks for ringing — when did the flatness start, and have you noticed anything else changing?", score: 3, good: "Opens the history and invites the owner to volunteer more signs." },
          { t: "She's probably just having one of those off days they all get now and then, so keep a casual eye on her at home and she'll likely perk up soon.", score: 0, harmful: true, bad: "Dismissive of an unwell entire bitch." },
          { t: "Just so I can note it on her record, can you remember roughly when she had her last season or whether there have been any recent changes to her cycle?", score: 2, bad: "A relevant question, but lead by characterising the illness first." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She finished a season about three weeks ago. There's a bit of smelly discharge from down there, and she's drinking buckets of water.",
        chips: [
          { t: "smelly discharge from down there", flag: true, coach: "Purulent vulval discharge weeks after a season is the hallmark of open pyometra." },
          { t: "drinking buckets of water", flag: true, coach: "Marked polydipsia (PU/PD) fits the toxaemia of pyometra and possible sepsis." },
        ],
        options: [
          { t: "Three weeks post-season with discharge and increased drinking is important — is she eating, and has she vomited or seemed feverish?", score: 3, good: "Connects the timeline to pyometra and screens for systemic illness." },
          { t: "Oh dear — is that discharge causing you much of a problem with your carpets and her bedding at home, or is it fairly easy to keep on top of?", score: 0, harmful: true, bad: "Trivialises a serious sign and wastes time." },
          { t: "Just to make sure our records are completely up to date, can you confirm for me whether she has actually been spayed at some point in the past?", score: 1, bad: "Already answered — she's entire; move on to systemic signs." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "She's gone off her dinner and seems a bit wobbly now. Should I just see how she is tomorrow?",
        chips: [{ t: "a bit wobbly", flag: false }],
        options: [
          { t: "No — those signs in an entire bitch point to a womb infection that can become life-threatening. Bring her straight in; don't offer food in case she needs surgery.", score: 3, good: "Names the concern, escalates appropriately and pre-empts surgery." },
          { t: "Try tempting her with a small, light meal this evening to keep her strength up, and we can always reassess how she's doing first thing in the morning.", score: 0, harmful: true, bad: "Overnight delay with possible sepsis is dangerous." },
          { t: "Let's get her booked in for a routine spay consultation sometime next week, so we can talk through getting her desexed properly then.", score: 0, harmful: true, bad: "Far too slow for a sick pyometra patient." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "An unwell entire bitch with post-season discharge and PU/PD is a suspected pyometra — an emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Post-season discharge, PU/PD and lethargy in an entire bitch = suspected pyometra, a surgical emergency." },
        { t: "Urgent — today", score: 12 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "blocked-cat",
    caller: "Mr Okafor",
    patient: "Tiger",
    signalment: "DSH, 3y MN, ~5 kg",
    demeanour: "Worried",
    summary: "Tom cat going to the tray repeatedly with nothing coming out.",
    coachTitle: "Suspected urethral obstruction — male cat (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Tiger keeps going to his litter tray but nothing's happening. He's been crying in there.",
        chips: [
          { t: "nothing's happening", flag: true, coach: "Straining with no urine in a male cat is a blocked-bladder emergency." },
          { t: "crying in there", flag: true, coach: "Vocalising while straining signals pain and likely obstruction." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — that's important to check quickly. Is he a male cat, and is any urine coming out at all?", score: 3, good: "Recognises the risk and confirms sex and output immediately." },
          { t: "Cats can be terribly fussy about their litter trays, can't they — try changing the litter type and giving the tray a really good clean and see if that helps.", score: 0, harmful: true, bad: "Dangerously dismissive of a possible obstruction." },
          { t: "While I've got his record up here in front of me, can I just quickly check whether he's been wormed recently and is up to date with his flea treatment?", score: 1, bad: "Irrelevant to an acute straining cat." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Yes, he's a neutered male. I haven't seen any wee for almost a day, and now he's licking himself and won't eat.",
        chips: [
          { t: "almost a day", flag: true, coach: "No urine for ~24 h in a male cat is a true emergency — bladder rupture and fatal electrolyte changes loom." },
          { t: "won't eat", flag: false },
        ],
        options: [
          { t: "A male cat not passing urine for that long is an emergency. Is his tummy tense or is he crying when he tries?", score: 3, good: "Escalates correctly and checks for a painful, distended bladder." },
          { t: "Try tempting him with a little tuna or some of his favourite food to perk him up a bit, and hopefully he'll start using the tray normally again soon.", score: 0, harmful: true, bad: "Feeding delays critical care for an obstructed cat." },
          { t: "Would you be able to collect a small urine sample from his tray first and drop it in, so the vet has something to test before you bring him down?", score: 0, harmful: true, bad: "Impossible and time-wasting if he's blocked." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "He does seem really uncomfortable. What should I do?",
        chips: [],
        options: [
          { t: "Bring him in immediately — a blocked bladder is rapidly life-threatening. Pop him in his carrier and come now; I'll let the vet know you're on your way.", score: 3, good: "Clear, urgent, safe instruction with a heads-up to the team." },
          { t: "Let's give it another few hours at home and see whether he manages to pass anything into the tray before we think about booking him in to be seen.", score: 0, harmful: true, bad: "Even a few hours can be fatal with a full obstruction." },
          { t: "You could try gently massaging his tummy and bladder area at home to encourage him to go, and that might help him pass some urine on his own.", score: 0, harmful: true, bad: "Unsafe — risks rupturing an over-distended bladder." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A male cat straining with no urine output is a urethral obstruction — an immediate 'see now' emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "A blocked male cat is one of the most time-critical small-animal emergencies." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "itchy-skin",
    caller: "Ms Bianchi",
    patient: "Coco",
    signalment: "Cavoodle, 4y FN, ~9 kg",
    demeanour: "Relaxed",
    summary: "Scratching for a couple of weeks; otherwise bright and eating well.",
    coachTitle: "Chronic itch / suspected allergy — dog (routine–urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Coco's been a bit scratchy for a couple of weeks. She's fine in herself, just itchy.",
        chips: [{ t: "scratchy for a couple of weeks", flag: false }, { t: "fine in herself", flag: false }],
        options: [
          { t: "Riverside Vet, Sam speaking — happy to help. Where is she scratching most, and is the skin red, scabby or smelly?", score: 3, good: "Characterises the itch and screens for secondary infection." },
          { t: "Honestly, all dogs have a good scratch from time to time and it's rarely anything to fuss over, so I really wouldn't worry yourself about it too much.", score: 0, harmful: true, bad: "Dismissive; chronic itch deserves assessment even if not urgent." },
          { t: "It sounds like she probably just needs a good bath with a nice gentle shampoo to freshen her up and take the itch away for a little while.", score: 1, bad: "Premature advice before any history." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Mostly her paws and tummy. There's a little pink skin but no sores. She's up to date with flea treatment.",
        chips: [{ t: "up to date with flea treatment", flag: false }],
        options: [
          { t: "Good that fleas are covered. Has anything changed — new food, walks or environment — and is she losing any hair?", score: 3, good: "Explores allergy triggers and checks for hair loss/secondary change." },
          { t: "Alongside all the scratching, have you noticed her vomiting, having any diarrhoea or going a bit quiet and off her food in herself at all recently?", score: 2, bad: "Useful screening, though less targeted to a skin complaint." },
          { t: "In the meantime you could just dab a little of your own antihistamine or hydrocortisone cream onto the itchy patches to take the edge off it for her.", score: 0, harmful: true, bad: "Never advise human medication without veterinary direction." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "So do I need to rush her in or can it wait?",
        chips: [],
        options: [
          { t: "She's comfortable and eating, so this isn't an emergency — let's book a skin consult in the next few days, and avoid bathing or creams until the vet sees her.", score: 3, good: "Appropriate routine triage with sensible interim advice." },
          { t: "I really think you should bring her straight in as an emergency right now, so the vet can take an urgent look at her before this gets any worse.", score: 0, bad: "Over-triage — ties up emergency slots for a stable, comfortable dog." },
          { t: "Honestly, I'd just ignore it for now — these itchy little patches nearly always clear up on their own given a bit of time, so there's no need to come in.", score: 0, harmful: true, bad: "Dismissive — chronic itch usually needs investigation." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A bright, eating dog with a two-week itch is a routine booking — neither an emergency nor something to ignore.",
      options: [
        { t: "Routine — book in", score: 25, why: "A comfortable, systemically well dog with chronic itch is a routine skin consult." },
        { t: "Urgent — today", score: 12 },
        { t: "Advice & monitor at home", score: 14 },
        { t: "Emergency — see now", score: 0 },
      ],
    },
  },

  {
    id: "gdv-bloat",
    caller: "Mr Stavros",
    patient: "Zeus",
    signalment: "Great Dane, 6y MN, ~58 kg",
    demeanour: "Frightened",
    summary: "Tried to vomit several times with nothing up; belly looks bigger.",
    coachTitle: "Suspected GDV / bloat — large-breed dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Zeus keeps trying to be sick but nothing comes up, and his tummy looks bigger than usual.",
        chips: [
          { t: "trying to be sick but nothing comes up", flag: true, coach: "Unproductive retching in a big dog is the classic sign of gastric dilatation-volvulus (GDV)." },
          { t: "tummy looks bigger", flag: true, coach: "A rapidly distending abdomen suggests bloat/GDV — minutes matter." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — that combination is serious in a big dog. When did it start, and is he restless or drooling?", score: 3, good: "Recognises GDV risk and checks onset and distress signs." },
          { t: "He's probably just got a bit of an upset stomach from something he's eaten, so I'd give it some time and he should settle down again before long.", score: 0, harmful: true, bad: "GDV is fatal within hours — never advise waiting." },
          { t: "Just so I can make a note of it on his record, have you changed his food or his feeding routine recently, or given him anything different to his usual?", score: 1, bad: "Diet history is secondary to recognising a surgical emergency." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It started about half an hour ago after he bolted his dinner. He's pacing, drooling and looks really uncomfortable.",
        chips: [
          { t: "bolted his dinner", flag: false },
          { t: "pacing, drooling", flag: true, coach: "Restlessness and hypersalivation are early GDV signs in deep-chested breeds." },
        ],
        options: [
          { t: "Those are signs of a twisted, bloated stomach — an emergency. Is his belly tight like a drum if you gently tap it?", score: 3, good: "Names GDV and checks for a tympanic abdomen while escalating." },
          { t: "Try taking him for a gentle walk around the block to help him pass some wind and burp it up, as that often relieves a bloated, gassy tummy.", score: 0, harmful: true, bad: "Exercise won't decompress a volvulus and delays surgery." },
          { t: "Offer him a nice big bowl of fresh water to help settle his stomach and wash everything down, and see if that eases the discomfort for him a little.", score: 0, harmful: true, bad: "Drinking worsens distension in suspected GDV." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "What do we do — he's getting worse?",
        chips: [],
        options: [
          { t: "Bring him in immediately, no food or water, drive straight here — I'm alerting the emergency team now so we're ready for him.", score: 3, good: "Correct, urgent instruction with the team primed for GDV." },
          { t: "Give him one of the antacid tablets you've got at home to settle his stomach acid, and then just keep a close eye on him for the next little while.", score: 0, harmful: true, bad: "Home medication wastes critical minutes." },
          { t: "Let's see how he's getting on in an hour or so once he's had a chance to settle, and you can always call us straight back if he's no better.", score: 0, harmful: true, bad: "GDV can be fatal within an hour." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Unproductive retching and a distending abdomen in a deep-chested dog is suspected GDV — an immediate emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Suspected GDV in a giant breed is one of the most time-critical emergencies." },
        { t: "Urgent — today", score: 6 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "chocolate-dog",
    caller: "Mr Patel",
    patient: "Bella",
    signalment: "Cockapoo, 3y FN, ~8 kg",
    demeanour: "Anxious",
    summary: "Found a chewed wrapper; ate dark chocolate ~30 min ago.",
    coachTitle: "Chocolate (theobromine) ingestion — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Hi, I think my dog has eaten some chocolate — I found a chewed-up wrapper on the floor.",
        chips: [{ t: "eaten some chocolate", flag: true, coach: "Chocolate contains theobromine, which is toxic to dogs — risk depends on the type and amount." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's work out the risk. What kind of chocolate was it, and roughly how much?", score: 3, good: "Calm opening that gathers the two facts that decide toxicity — type and dose." },
          { t: "A little bit of chocolate really won't do any harm to a dog of that size, so honestly I wouldn't go worrying yourself about it at all.", score: 0, harmful: true, bad: "Falsely reassuring before any dose or type is known." },
          { t: "Just keep a close eye on her for any vomiting over the next few hours, and only ring us back if she actually starts to seem unwell in herself.", score: 1, bad: "Premature advice — the dose must be assessed first." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It was a big block of dark chocolate — about 200 grams — and she's only small. She had it maybe half an hour ago.",
        chips: [
          { t: "dark chocolate", flag: true, coach: "Dark and cooking chocolate carry the most theobromine — far more dangerous than milk chocolate." },
          { t: "half an hour ago", flag: true, coach: "Recent ingestion (<1 h) means we can still make her vomit to decontaminate." },
        ],
        options: [
          { t: "That's a significant amount of dark chocolate for a small dog. Do you have the wrapper so we can check the exact weight and cocoa content?", score: 3, good: "Quantifies the toxic dose accurately using the packaging." },
          { t: "Just out of interest, has she managed to get hold of chocolate before in the past and been completely fine afterwards without any problems at all?", score: 1, bad: "Past tolerance doesn't change the current toxic dose." },
          { t: "Give her plenty of fresh water to drink and keep her nice and quiet at home tonight, and she should have flushed most of it through by the morning.", score: 0, harmful: true, bad: "Home monitoring wastes the decontamination window." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "She seems fine at the moment, just licking her lips. What should I do?",
        chips: [],
        options: [
          { t: "Bring her in now — at that dose we'll want to make her vomit and start treatment before signs appear. Bring the wrapper.", score: 3, good: "Correct, urgent decontamination advice with the packaging." },
          { t: "Try making her sick at home first by giving her a good spoonful of salt water, and that should bring most of the chocolate straight back up again.", score: 0, harmful: true, bad: "Salt water is dangerous and can cause salt toxicity." },
          { t: "Let's get her booked in for a check-up tomorrow if she's still seeming unwell by then, but otherwise she's probably fine to just monitor at home.", score: 1, bad: "Too slow — signs may appear before then." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A large dose of dark chocolate in a small dog is a time-critical toxin ingestion — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "A large dose of dark chocolate in a small dog is a time-critical toxin ingestion." },
        { t: "Urgent — today", score: 10 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "dystocia",
    caller: "Mrs Bell",
    patient: "Daisy",
    signalment: "French Bulldog, 3y F entire, ~12 kg",
    demeanour: "Worried",
    summary: "In labour; straining over 40 minutes with no puppy.",
    coachTitle: "Dystocia / whelping difficulty — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's having puppies but she's been pushing for ages and nothing's come out.",
        chips: [{ t: "pushing for ages and nothing's come out", flag: true, coach: "Strong straining with no pup produced is dystocia — a time-critical obstetric emergency." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's check her carefully. How long has she been actively straining, and is this her first litter?", score: 3, good: "Establishes the duration of straining and parity straight away." },
          { t: "First-time mums are often quite slow with their very first litter, so just give her plenty of space and time and let nature take its course.", score: 0, harmful: true, bad: "Dangerous delay — prolonged straining risks the litter and the dam." },
          { t: "Try giving her a little bowl of warm milk to help her relax and settle, as that sometimes seems to get things moving along nicely for them.", score: 1, bad: "Unhelpful home remedy that delays assessment." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She's been straining hard for about forty minutes. She's a French Bulldog and this is her first litter.",
        chips: [
          { t: "forty minutes", flag: true, coach: "More than 30 minutes of strong straining without a pup needs urgent veterinary help." },
          { t: "French Bulldog", flag: true, coach: "Brachycephalic breeds are very prone to dystocia and often need a caesarean." },
        ],
        options: [
          { t: "Forty minutes of hard straining in a Frenchie is concerning. Is there any discharge, and can you see anything at the vulva?", score: 3, good: "Screens for the warning signs that confirm an obstructed labour." },
          { t: "Just while I've got her record open in front of me, can I quickly check whether she's up to date with her worming and her vaccinations as well?", score: 1, bad: "Not relevant during an obstructed labour." },
          { t: "If you can already see the puppy there, try reaching in and gently pulling it out yourself during one of her pushes to help move things along.", score: 0, harmful: true, bad: "Unsafe — risks injuring the dam and the pup." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "There's a bit of green discharge but no puppy. What now?",
        chips: [{ t: "green discharge", flag: true, coach: "Green discharge before the first pup signals placental separation — the litter is at risk." }],
        options: [
          { t: "Green discharge before a pup means we need to see her straight away — bring her in now, she may need a caesarean.", score: 3, good: "Recognises placental separation and escalates to immediate care." },
          { t: "Let's wait another hour and see whether a puppy comes along on its own first, as these things often sort themselves out once she really gets going.", score: 0, harmful: true, bad: "An hour's delay can be fatal for the litter." },
          { t: "Why don't you pop her in tomorrow morning so we can check her and the puppies over properly once she's had the rest of the litter overnight.", score: 1, bad: "Far too slow for an obstructed labour." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Prolonged straining with green discharge and no pup is dystocia — an obstetric emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Prolonged straining with green discharge and no pup is dystocia — an obstetric emergency." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "snake-bite",
    caller: "Mr Reid",
    patient: "Banjo",
    signalment: "Kelpie, 5y MN, ~20 kg",
    demeanour: "Frightened",
    summary: "Collapsed after yelping in long grass; possible snake.",
    coachTitle: "Suspected snake envenomation — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog yelped in the long grass and now he's collapsed and wobbly!",
        chips: [{ t: "collapsed and wobbly", flag: true, coach: "Sudden collapse and weakness after a yelp in grass is a classic snake-envenomation sign." }],
        options: [
          { t: "Riverside Vet, Sam speaking — keep him as still as you can. Did you see a snake or any puncture wounds?", score: 3, good: "Gives the key first-aid instruction (keep still) and gathers history." },
          { t: "He's probably just worn himself out from all the running about in this heat, so let him have a good rest in the shade and he'll soon bounce back.", score: 0, harmful: true, bad: "Dismisses a potentially lethal envenomation." },
          { t: "Offer him a big drink of water and let him have a quiet lie-down for a bit, and see whether he gradually perks back up once he's cooled off.", score: 1, bad: "Delays urgent care for suspected envenomation." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "I think I saw a brown snake slither off. He's drooling and his back legs keep giving way.",
        chips: [
          { t: "brown snake", flag: true, coach: "Brown snakes are highly venomous — envenomation is rapidly life-threatening." },
          { t: "back legs keep giving way", flag: true, coach: "Ascending weakness/paralysis is a hallmark of elapid snake envenomation." },
        ],
        options: [
          { t: "That sounds like a snake bite. Keep him calm and still — movement spreads venom. Can someone bring the car to him?", score: 3, good: "Correct envenomation first aid — immobilise and minimise movement." },
          { t: "Just so I can update his notes properly, can I check whether he's had all of his vaccinations this year and is up to date with everything else?", score: 1, bad: "Not the priority during suspected envenomation." },
          { t: "If it's at all safe to do so, try to catch the snake or get a clear photo of it so that we can work out exactly what species it was that bit him.", score: 0, harmful: true, bad: "Dangerous and unnecessary — risks a second bite." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I try to suck out the venom or put a tourniquet on?",
        chips: [],
        options: [
          { t: "No — don't cut, suck or tourniquet. Keep him still and bring him straight in; we have antivenom ready. Carry him to the car if you can.", score: 3, good: "Corrects dangerous myths and gives safe, urgent instructions." },
          { t: "Yes, find something you can tie tightly as a tourniquet just above the bite wound to try and stop the venom from spreading any further up his leg.", score: 0, harmful: true, bad: "Tourniquets cause harm and are not recommended for snake bites." },
          { t: "Give the bite a good wash with plenty of clean water and keep a close eye on him at home for an hour or so to see whether he settles down again.", score: 1, bad: "Watchful waiting is dangerous with envenomation." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Suspected brown-snake envenomation with collapse and paralysis is an immediate emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Suspected brown-snake envenomation with collapse and paralysis is an immediate emergency." },
        { t: "Urgent — today", score: 6 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "heatstroke",
    caller: "Ms Flynn",
    patient: "Boomer",
    signalment: "Labrador, 4y MN, ~32 kg",
    demeanour: "Panicked",
    summary: "Collapsed after a run on a hot day; panting heavily.",
    coachTitle: "Heatstroke — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "We were out running and now he's collapsed and panting really hard — it's so hot today.",
        chips: [{ t: "collapsed and panting really hard", flag: true, coach: "Collapse with heavy panting after exercise in heat strongly suggests heatstroke." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's cool him quickly. Where are you, and is he able to stand or responding to you?", score: 3, good: "Prioritises cooling and assesses mentation immediately." },
          { t: "Labradors absolutely love the heat, don't they — just give him a minute or two lying in the shade to catch his breath and he'll be right as rain.", score: 0, harmful: true, bad: "Dismisses a rapidly fatal condition." },
          { t: "Offer him a drink of water from your bottle and then just carry on walking him home nice and slowly, and he should cool off again on the way back.", score: 1, bad: "Under-reacts to a collapsed, overheated dog." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's barely responding, his gums look really red and he's bringing up a bit of saliva. It's about 38 degrees out.",
        chips: [
          { t: "barely responding", flag: true, coach: "Reduced consciousness in a hot, collapsed dog indicates severe heatstroke." },
          { t: "gums look really red", flag: true, coach: "Brick-red gums are a sign of heatstroke and poor perfusion." },
        ],
        options: [
          { t: "He needs cooling now. Pour cool — not ice-cold — water over him, especially his belly, and get him into shade while you bring him in.", score: 3, good: "Gives correct active-cooling first aid en route." },
          { t: "Just so I've got the full picture for the vet, what did he have for his breakfast this morning and roughly how long was that before you set off?", score: 1, bad: "Irrelevant while he is critically overheated." },
          { t: "Wrap him up snugly in a warm blanket to keep him calm and comfortable while you make your way over to us with him in the car.", score: 0, harmful: true, bad: "Insulating an overheated dog worsens heatstroke." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Okay, I'm wetting him down. Do I still need to come in?",
        chips: [],
        options: [
          { t: "Yes — keep wetting him and drive straight in with air-con on. Heatstroke can damage organs even once he looks cooler.", score: 3, good: "Correct: cool and transport; organ damage can be delayed." },
          { t: "If he starts looking a lot brighter once you've wet him down and cooled him off properly, then you're probably fine to just keep him home and rest him.", score: 0, harmful: true, bad: "Heatstroke complications can develop hours later." },
          { t: "Give him an hour or so in a cool, quiet room to settle down properly, and then ring us back to let us know whether he's started to come good.", score: 1, bad: "Too slow for a critical heat emergency." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Collapse, brick-red gums and dull mentation after exercise in heat is heatstroke — an emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Collapse, brick-red gums and dull mentation after exercise in heat is heatstroke — an emergency." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "rta-cat",
    caller: "Mr Nowak",
    patient: "Pip",
    signalment: "DSH, 2y MN, ~4.5 kg",
    demeanour: "Distressed",
    summary: "Cat hit by a car; crawled home, dragging a back leg.",
    coachTitle: "Road traffic trauma / shock — cat (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My cat's just been hit by a car — he managed to crawl home but he's in a bad way.",
        chips: [{ t: "hit by a car", flag: true, coach: "Any road-traffic trauma needs urgent assessment for internal injury and shock." }],
        options: [
          { t: "Riverside Vet, Sam speaking — I'm so sorry. Let's get him seen fast. Is he breathing normally and can you see any bleeding?", score: 3, good: "Empathic and screens the two life-threats — breathing and haemorrhage." },
          { t: "Cats are incredibly tough little things, aren't they — if he's managed to crawl all the way home by himself then he's most likely going to be just fine.", score: 0, harmful: true, bad: "Crawling home does not rule out serious internal injury." },
          { t: "Just keep him somewhere warm and quiet for tonight and see how he's getting on in the morning, and we can take it from there if he's no better.", score: 1, bad: "Dangerous delay after major trauma." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's breathing fast and shallow, dragging a back leg, and his gums look pale. He hid under the bed.",
        chips: [
          { t: "breathing fast and shallow", flag: true, coach: "Rapid shallow breathing after trauma can mean chest injury or shock." },
          { t: "gums look pale", flag: true, coach: "Pale gums suggest blood loss or shock — a red flag after a car accident." },
        ],
        options: [
          { t: "Pale gums and fast breathing mean he could be in shock. Slide him into a carrier with minimal handling and come straight in.", score: 3, good: "Recognises shock and advises gentle, immediate transport." },
          { t: "Just while I bring his record up on screen, can you remind me roughly when he last had his vaccinations and whether he's microchipped at all?", score: 1, bad: "Not relevant to an actively shocked trauma patient." },
          { t: "Before you move him at all, try to splint that injured back leg yourself with something firm so that it doesn't flop about and hurt him on the way in.", score: 0, harmful: true, bad: "Home splinting causes pain and can worsen injuries." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I give him something for the pain at home?",
        chips: [],
        options: [
          { t: "No — never give human painkillers to cats, many are fatal. Keep him quiet and warm and bring him in now so we can stabilise him.", score: 3, good: "Prevents a fatal medication error and escalates appropriately." },
          { t: "You could give him a small dose of your own paracetamol to take the edge off the pain before you set off, just to make him a bit more comfortable.", score: 0, harmful: true, bad: "Paracetamol is lethal to cats — never advise it." },
          { t: "Let him stay tucked up resting under the bed where he feels safe for now, and just check on him every so often through the rest of the evening.", score: 1, bad: "Delays critical stabilisation." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Road trauma with pale gums and laboured breathing is shock — an immediate emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Road trauma with pale gums and laboured breathing is shock — an immediate emergency." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "bee-sting",
    caller: "Mrs Akhtar",
    patient: "Coco",
    signalment: "Pug, 2y FN, ~9 kg",
    demeanour: "Mildly worried",
    summary: "Swollen face after sniffing the garden; comfortable, breathing well.",
    coachTitle: "Allergic facial swelling / insect sting — dog (urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's face has puffed up after she was sniffing around the garden — looks like she's been stung.",
        chips: [{ t: "face has puffed up", flag: true, coach: "Facial swelling after a likely sting is an allergic reaction — watch for airway involvement." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's check she's comfortable. Is she breathing okay, and is the swelling getting worse quickly?", score: 3, good: "Triages the one thing that escalates a sting — the airway." },
          { t: "Facial swelling like that nearly always goes back down on its own within a few hours, so honestly I'd just ignore it for now and leave her be.", score: 0, harmful: true, bad: "Allergic swelling can progress to airway compromise." },
          { t: "Just give her one of the antihistamine tablets from your own medicine cupboard to bring the swelling down, and then sit tight and wait and see.", score: 1, bad: "Dosing human medication without veterinary direction is unsafe." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She's breathing fine and still wagging, but her muzzle and one eye are puffy. It started about twenty minutes ago.",
        chips: [
          { t: "breathing fine", flag: false },
          { t: "muzzle and one eye are puffy", flag: true, coach: "Localised facial swelling is an allergic reaction; airway is currently patent but needs watching." },
        ],
        options: [
          { t: "Good that she's breathing comfortably. Watch her breathing closely — if her throat swells, that's an emergency. Let's get her seen today.", score: 3, good: "Correctly safety-nets for airway signs while booking same-day care." },
          { t: "Just so I can note it down for the vet, has she eaten anything unusual or got into anything different out in the garden or the house earlier today?", score: 1, bad: "Reasonable, but the priority is monitoring the reaction." },
          { t: "As long as she's still breathing perfectly fine in herself, you can quite safely just leave that swelling alone for a few days and see if it goes down.", score: 0, harmful: true, bad: "Allergic reactions can worsen — leaving it is unsafe." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Do I need to rush her in right now?",
        chips: [],
        options: [
          { t: "She's stable, so it's not a 999 emergency, but allergic swelling can progress — bring her in today so we can settle it and watch her.", score: 3, good: "Proportionate: same-day care with clear safety-netting." },
          { t: "Honestly there's no need to come in at all for this — that sort of swelling almost always settles right back down by itself once the reaction passes.", score: 0, harmful: true, bad: "Misses a reaction that could deteriorate." },
          { t: "Why don't you bring her in sometime next week whenever it happens to suit you, and we can have a proper look at that swelling for you then.", score: 1, bad: "Too slow for an evolving allergic reaction." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Stable allergic facial swelling needs same-day treatment with safety-netting — not a 'see now' nor a 'leave it'.",
      options: [
        { t: "Emergency — see now", score: 14 },
        { t: "Urgent — today", score: 25, why: "Stable facial swelling from an allergic reaction needs same-day treatment, with safety-netting for airway signs." },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 6 },
      ],
    },
  },

  {
    id: "proptosis",
    caller: "Mr Davies",
    patient: "Gizmo",
    signalment: "Shih Tzu, 6y MN, ~7 kg",
    demeanour: "Alarmed",
    summary: "Eye bulging out of the socket after a scuffle with another dog.",
    coachTitle: "Proptosed eye — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog had a scuffle and now his eye looks like it's bulging right out of his head!",
        chips: [{ t: "bulging right out of his head", flag: true, coach: "A proptosed (out-of-socket) eye is a sight-threatening emergency, common in flat-faced breeds." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's protect that eye. Don't let him rub it. Can you keep it moist with clean water or saline?", score: 3, good: "Protects the eye and gives correct first aid (keep it moist)." },
          { t: "Before you do anything else, try to gently push the eye back into its socket yourself with a clean damp cloth so that it's back where it should be.", score: 0, harmful: true, bad: "Pushing it back causes further damage — leave it to the vet." },
          { t: "Hold a cold compress gently against the eye for a little while to bring the swelling down, and then just keep a close eye on how it's looking.", score: 1, bad: "Delays the urgent care needed to save the eye." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It happened just now in a fight. He's a Shih Tzu and he keeps trying to paw at it.",
        chips: [{ t: "keeps trying to paw at it", flag: true, coach: "Self-trauma worsens a proptosis and reduces the chance of saving the eye." }],
        options: [
          { t: "Stop him pawing it — pop a buster collar or improvised cone on if you have one — and keep the eye damp with saline. Come straight in.", score: 3, good: "Prevents self-trauma and keeps the eye viable for transport." },
          { t: "Just so I can pull his full record up on our system, can you confirm whether he's microchipped and currently registered to you at this address?", score: 1, bad: "Not relevant to an acute eye emergency." },
          { t: "Let him settle down somewhere nice and quiet and dark for tonight, and then bring him in to see one of the vets first thing tomorrow morning.", score: 0, harmful: true, bad: "Delay greatly reduces the chance of saving the eye." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Is it an emergency or can it wait?",
        chips: [],
        options: [
          { t: "It's an emergency — the sooner we see him, the better the chance of saving the eye. Keep it moist and bring him now.", score: 3, good: "Clear, correct urgency with a protective instruction." },
          { t: "Eyes always look terribly dramatic when they're like that but they very often settle by themselves, so I'd just wait and see how it looks tomorrow.", score: 0, harmful: true, bad: "A proptosis will not 'settle' — it needs surgery quickly." },
          { t: "Let's get him booked in for a routine eye check at some point later this week, so the vet can take a proper look once it's all calmed down a bit.", score: 1, bad: "Far too slow for a proptosed eye." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A proptosed eye is sight-threatening and needs surgery as soon as possible — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "A proptosed eye is sight-threatening and needs surgery as soon as possible." },
        { t: "Urgent — today", score: 10 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "grape-dog",
    caller: "Ms Romano",
    patient: "Teddy",
    signalment: "Cavalier, 4y MN, ~9 kg",
    demeanour: "Casual",
    summary: "Ate a handful of grapes off the counter about an hour ago.",
    coachTitle: "Grape / raisin ingestion — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog jumped up and ate some grapes off the kitchen counter. They're just fruit though, right?",
        chips: [
          { t: "ate some grapes", flag: true, coach: "Grapes and raisins can cause acute kidney failure in dogs, even in small amounts." },
          { t: "just fruit though", flag: false },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — grapes can actually be toxic to dogs, so I'm glad you rang. Roughly how many did he eat, and when?", score: 3, good: "Corrects the misconception and gathers dose and timing." },
          { t: "Grapes are actually really healthy little snacks full of vitamins, aren't they, so there's honestly nothing at all to worry about with him having a few.", score: 0, harmful: true, bad: "Clinically wrong — grapes are nephrotoxic to dogs." },
          { t: "Just keep a close eye on him for the rest of the day, and only give us a ring back if he actually starts being sick or seems unwell in himself.", score: 1, bad: "Misses the decontamination window." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Maybe a handful — about ten grapes — around an hour ago. He's a small spaniel.",
        chips: [
          { t: "about ten grapes", flag: true, coach: "Even a handful can be toxic to a small dog — the toxic dose is unpredictable." },
          { t: "around an hour ago", flag: true, coach: "Within a couple of hours we can make him vomit to decontaminate." },
        ],
        options: [
          { t: "For a dog his size that's enough to be concerned. Do you still have the packet, and has he vomited at all?", score: 3, good: "Assesses dose relative to bodyweight and screens for early signs." },
          { t: "Just out of interest, has he managed to eat grapes or raisins before in the past and been completely fine afterwards without any trouble at all?", score: 1, bad: "Prior exposure doesn't reduce today's risk." },
          { t: "Give it a day or so and just keep an eye on whether his appetite or his drinking changes at all, and ring us back if anything seems off to you.", score: 0, harmful: true, bad: "Kidney injury is silent at first — waiting is dangerous." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "He seems perfectly happy though. Do I really need to bring him in?",
        chips: [],
        options: [
          { t: "Yes — kidney damage from grapes is silent at first. Bring him in now so we can make him vomit and start fluids.", score: 3, good: "Correct, urgent decontamination despite a well-looking dog." },
          { t: "If he still seems perfectly happy in himself, just keep an eye on him at home and only bring him in if he goes off his food over the next day or two.", score: 0, harmful: true, bad: "By the time he's unwell, kidney damage may be done." },
          { t: "Let's get him booked in for a general check-up in a couple of days' time, once we've had a chance to see how he's getting on at home.", score: 1, bad: "Too slow to prevent toxicity." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Grape ingestion in a small dog risks acute kidney failure — decontaminate now (see now).",
      options: [
        { t: "Urgent — today", score: 10 },
        { t: "Emergency — see now", score: 25, why: "Grape ingestion risks acute kidney failure and needs immediate decontamination." },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "xylitol-dog",
    caller: "Mr Schmidt",
    patient: "Rosie",
    signalment: "Beagle, 3y FN, ~12 kg",
    demeanour: "Worried",
    summary: "Ate sugar-free gum containing xylitol about 20 minutes ago.",
    coachTitle: "Xylitol ingestion — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog got into my handbag and ate some sugar-free chewing gum.",
        chips: [{ t: "sugar-free chewing gum", flag: true, coach: "Sugar-free gum often contains xylitol, which causes dangerous hypoglycaemia and liver injury in dogs." }],
        options: [
          { t: "Riverside Vet, Sam speaking — some sugar-free gum contains xylitol, which is toxic to dogs. Can you check the packet for 'xylitol'?", score: 3, good: "Identifies the specific toxin risk and confirms it from the label." },
          { t: "Chewing gum normally just passes straight through them without causing any real bother at all, so honestly I really wouldn't worry yourself about it.", score: 0, harmful: true, bad: "Ignores a potentially fatal toxin." },
          { t: "Just keep a close eye out for any tummy upset or vomiting over the next little while, and feel free to call us back again if you need to.", score: 1, bad: "Misses the urgent decontamination window." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Yes — it says xylitol on the packet. She had several pieces about twenty minutes ago.",
        chips: [
          { t: "says xylitol on the packet", flag: true, coach: "Confirmed xylitol ingestion can drop blood sugar within 30–60 minutes." },
          { t: "twenty minutes ago", flag: true, coach: "Very recent — decontamination and glucose monitoring are time-critical." },
        ],
        options: [
          { t: "That's confirmed xylitol, which is an emergency. Has she seemed wobbly, weak or trembly at all?", score: 3, good: "Confirms the toxin and screens for hypoglycaemia." },
          { t: "Just so I understand her a bit better, is she normally quite a greedy dog who's always getting into things she shouldn't around the house?", score: 1, bad: "Irrelevant to managing the toxin." },
          { t: "Give her a nice big meal now to help soak up all the gum in her stomach, and then just sit tight at home and wait to see how she gets on.", score: 0, harmful: true, bad: "Feeding and waiting delays critical treatment." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "She seems normal right now. What should I do?",
        chips: [],
        options: [
          { t: "Bring her straight in — her blood sugar can crash suddenly. We'll monitor and treat her; bring the gum packet.", score: 3, good: "Correct: urgent assessment because signs are delayed and abrupt." },
          { t: "Rub a little honey onto her gums to help keep her blood sugar up, and then just keep her quietly at home where you can watch her closely.", score: 0, harmful: true, bad: "Home management of a fast-acting toxin is unsafe." },
          { t: "Why don't we get her booked in for first thing tomorrow morning, and you can keep a close eye on her at home overnight in the meantime.", score: 1, bad: "Too slow — hypoglycaemia can occur within the hour." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Confirmed xylitol ingestion can cause sudden hypoglycaemia and liver injury — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Confirmed xylitol ingestion can cause sudden hypoglycaemia and liver injury — see now." },
        { t: "Urgent — today", score: 8 },
        { t: "Advice & monitor at home", score: 0, harmful: true },
        { t: "Routine — book in", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "rodenticide",
    caller: "Mrs Lowe",
    patient: "Scout",
    signalment: "Border Collie, 2y MN, ~18 kg",
    demeanour: "Concerned",
    summary: "Found chewing rat bait in the shed 15 minutes ago.",
    coachTitle: "Anticoagulant rodenticide ingestion — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "I caught my dog chewing on some rat poison in the shed.",
        chips: [{ t: "chewing on some rat poison", flag: true, coach: "Anticoagulant rodenticide causes fatal internal bleeding days later — early treatment is vital." }],
        options: [
          { t: "Riverside Vet, Sam speaking — that needs prompt action. Do you have the rat bait packet so we can identify the type?", score: 3, good: "Acts promptly and identifies the bait type for the correct antidote." },
          { t: "If he seems completely fine in himself right now then the poison probably hasn't really affected him, so I honestly wouldn't be too worried at this stage.", score: 0, harmful: true, bad: "Signs are delayed by days — 'seems fine' is meaningless now." },
          { t: "Just keep a close watch on him for any signs of bleeding over the next while, and give us a call straight back if you happen to notice any at all.", score: 1, bad: "By the time bleeding appears, treatment is far harder." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It's a blue pellet bait — he had it maybe fifteen minutes ago. He looks completely normal.",
        chips: [
          { t: "blue pellet bait", flag: true, coach: "Blue/green pellets are typically anticoagulant baits that affect blood clotting." },
          { t: "fifteen minutes ago", flag: true, coach: "Very recent — we can make him vomit now to remove the bait." },
        ],
        options: [
          { t: "Good that you have the packet. Even though he looks fine, signs are delayed for days. Bring him in now so we can make him vomit.", score: 3, good: "Explains the delayed risk and advises immediate decontamination." },
          { t: "Just while I've got his record open in front of me, can I quickly check whether he's had his flea and worming treatment recently and is up to date?", score: 1, bad: "Not relevant to the poisoning." },
          { t: "I'd just wait and see whether he actually shows any symptoms over the next few days before bringing him in, rather than rushing him down now for nothing.", score: 0, harmful: true, bad: "Waiting for bleeding can be fatal." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "He's totally normal — surely I can just watch him?",
        chips: [],
        options: [
          { t: "No — by the time bleeding starts it can be too late. Come in now with the packet; vitamin K is very effective if we start early.", score: 3, good: "Correct: early intervention prevents life-threatening bleeding." },
          { t: "For now just keep a close eye out for any blood in his wee or his poo, or any unusual bruising, and let us know straight away if you spot any of that.", score: 0, harmful: true, bad: "Relying on visible bleeding is unsafe." },
          { t: "Why don't you just keep an eye on him at home for now and only bring him in if he's still seeming off colour in a few days' time.", score: 1, bad: "Far too slow for a clotting toxin." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Rat-bait ingestion needs decontamination now — bleeding signs are dangerously delayed.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Rat-bait ingestion needs decontamination now — anticoagulant bleeding is dangerously delayed." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "parvo-pup",
    caller: "Mr Ahmed",
    patient: "Rolo",
    signalment: "Staffy pup, 12 weeks, M entire, ~6 kg",
    demeanour: "Worried",
    summary: "Unvaccinated puppy, vomiting and bloody diarrhoea.",
    coachTitle: "Suspected parvovirus — puppy (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My puppy's really poorly — he's been vomiting and has bloody diarrhoea.",
        chips: [{ t: "bloody diarrhoea", flag: true, coach: "Vomiting with bloody diarrhoea in a young pup is a parvovirus red flag." }],
        options: [
          { t: "Riverside Vet, Sam speaking — that's worrying in a puppy. How old is he, and has he had any vaccinations yet?", score: 3, good: "Targets the two facts that flag parvo risk — age and vaccine status." },
          { t: "Puppies get little upset tummies all the time when they're growing, so try feeding him some plain boiled rice and chicken and he'll likely settle.", score: 0, harmful: true, bad: "Bloody diarrhoea in a pup is never just an upset tummy." },
          { t: "Just withhold all of his food for a day to give his little tummy a proper rest, and see whether the vomiting and diarrhoea settle down on their own.", score: 1, bad: "Watchful waiting risks a rapidly dehydrating pup." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's twelve weeks and hasn't had any vaccinations yet. He's flat, won't drink, and the diarrhoea smells awful.",
        chips: [
          { t: "hasn't had any vaccinations", flag: true, coach: "An unvaccinated pup is at high risk of parvovirus, which is often fatal without treatment." },
          { t: "won't drink", flag: true, coach: "Refusing water with vomiting and diarrhoea means rapid, dangerous dehydration in a small pup." },
        ],
        options: [
          { t: "An unvaccinated pup with bloody diarrhoea could have parvovirus. He needs to be seen now — and please keep him away from other dogs.", score: 3, good: "Recognises parvo risk and adds correct biosecurity advice." },
          { t: "Just so I can note it down for the vet, what sort of food have you been feeding him, and have you changed his diet around at all recently?", score: 1, bad: "Diet history is secondary to an emergency presentation." },
          { t: "You could give him a small amount of children's anti-diarrhoea medicine to help firm things up, and then just wait and see whether he starts to improve.", score: 0, harmful: true, bad: "Human medication is unsafe and delays treatment." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Is it really that urgent? He's just a bit quiet.",
        chips: [],
        options: [
          { t: "Yes — parvo pups can deteriorate within hours. Bring him straight in, and tell us on arrival so we can isolate him.", score: 3, good: "Conveys urgency and protects other patients from infection." },
          { t: "Just keep him quietly at home and keep gently tempting him with little bits of tasty food for a day or two until he picks back up again.", score: 0, harmful: true, bad: "Delay can be fatal for a parvo puppy." },
          { t: "Let's get him booked in for his first set of vaccinations later this week, and that should help protect him from picking anything else up.", score: 1, bad: "Misses an acute, life-threatening illness." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "An unvaccinated pup with bloody diarrhoea and dehydration is suspected parvovirus — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "An unvaccinated pup with bloody diarrhoea and dehydration is suspected parvovirus — see now." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "dyspnoea-cat",
    caller: "Ms Webb",
    patient: "Smokey",
    signalment: "DSH, 9y MN, ~5 kg",
    demeanour: "Frightened",
    summary: "Cat open-mouth breathing, hunched and not moving.",
    coachTitle: "Respiratory distress — cat (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My cat is breathing with his mouth open and it looks like a real struggle.",
        chips: [{ t: "breathing with his mouth open", flag: true, coach: "Open-mouth breathing in a cat is always an emergency — cats rarely pant." }],
        options: [
          { t: "Riverside Vet, Sam speaking — open-mouth breathing in a cat is serious. Keep him calm and don't stress him. Is his breathing fast and laboured?", score: 3, good: "Flags the emergency and minimises stress while gathering detail." },
          { t: "Cats do sometimes pant a little when they get too hot, just like dogs do, so try sitting him next to a fan to cool him down and he should settle soon.", score: 0, harmful: true, bad: "Cats almost never pant benignly — this is dangerous." },
          { t: "Just give him plenty of space and somewhere nice and quiet to himself for a little while, and see whether his breathing settles back down on its own.", score: 1, bad: "Respiratory distress in cats can't safely wait." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It's really fast and his sides are heaving. He's crouched low and won't move.",
        chips: [
          { t: "sides are heaving", flag: true, coach: "Marked respiratory effort signals significant distress." },
          { t: "crouched low and won't move", flag: true, coach: "A hunched, still posture is a classic feline respiratory-distress sign." },
        ],
        options: [
          { t: "He's in respiratory distress. Handle him as little as possible, pop him gently in a carrier, and bring him straight in — stress can be fatal.", score: 3, good: "Correct low-stress handling and immediate transport." },
          { t: "Just so I've got the full picture for his notes, is he mainly an indoor cat, or does he spend a lot of his time outside roaming around the neighbourhood?", score: 1, bad: "Not the priority for a cat struggling to breathe." },
          { t: "Try gently giving him a little water from a syringe to keep his mouth moist and help him along, and that might ease his breathing a little for him.", score: 0, harmful: true, bad: "Syringing a dyspnoeic cat risks aspiration and added stress." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I wait to see if he improves?",
        chips: [],
        options: [
          { t: "No — bring him in now with minimal handling. We may need to give him oxygen the moment he arrives, so ring us as you set off.", score: 3, good: "Correct urgency with a heads-up so oxygen is ready." },
          { t: "Why don't you wait an hour or so and see how he goes, and then if he's still struggling as badly as that you can bring him straight in to us.", score: 0, harmful: true, bad: "A struggling cat can decompensate within minutes." },
          { t: "Let's get him booked in for a check-up with one of the vets this afternoon, and just keep him nice and calm and quiet at home until then.", score: 1, bad: "Far too slow for respiratory distress." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A cat in respiratory distress with open-mouth breathing is an immediate emergency.",
      options: [
        { t: "Urgent — today", score: 6 },
        { t: "Emergency — see now", score: 25, why: "A cat in respiratory distress with open-mouth breathing is an immediate emergency." },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "cat-abscess",
    caller: "Mr Goddard",
    patient: "Tom",
    signalment: "DSH, 5y MN, ~6 kg",
    demeanour: "Matter-of-fact",
    summary: "Outdoor tom, facial swelling and a bit quiet after a fight.",
    coachTitle: "Cat-bite abscess — cat (urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My cat's got a swelling on his face and he's gone a bit quiet — he's always getting into fights.",
        chips: [{ t: "swelling on his face", flag: true, coach: "A painful swelling after a fight is likely a cat-bite abscess; quietness suggests infection." }],
        options: [
          { t: "Riverside Vet, Sam speaking — that sounds like it might be a bite abscess. Is the swelling hot or painful, and is he eating?", score: 3, good: "Characterises the swelling and screens for systemic illness." },
          { t: "Cats generally sort their own little fights out between themselves and heal up just fine, so I'd honestly just leave it alone and let it settle down.", score: 0, harmful: true, bad: "An abscess won't resolve alone and can make the cat unwell." },
          { t: "Just bathe the swelling gently with some warm salt water at home and keep an eye on it for a week or so to see whether it goes down by itself.", score: 1, bad: "A week's delay risks the abscess worsening." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It's warm and he flinches when I touch it. He picked at his dinner but he's a bit off. No bleeding yet.",
        chips: [
          { t: "flinches when I touch it", flag: true, coach: "Pain on touch with a warm swelling supports an abscess needing treatment." },
          { t: "a bit off", flag: false },
        ],
        options: [
          { t: "That's typical of an abscess, which usually needs draining and antibiotics. Is he up to date with his vaccinations, and drinking okay?", score: 3, good: "Plans likely treatment and checks hydration and FIV/FeLV risk." },
          { t: "Just so I can describe him properly on our system, what colour is his collar and roughly what does he look like in himself for the record?", score: 1, bad: "Irrelevant to the clinical problem." },
          { t: "Try squeezing the swelling firmly between your fingers to pop it and drain all the pus out yourself, and that should relieve the pressure for him.", score: 0, harmful: true, bad: "Home squeezing is painful and spreads infection." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Can it wait or does he need to be seen?",
        chips: [],
        options: [
          { t: "He's stable but it won't clear on its own — let's book him in today to drain it and start antibiotics before it worsens.", score: 3, good: "Proportionate same-day care for an infected, painful swelling." },
          { t: "Just leave the swelling to burst and drain by itself in its own time, and only bring him in if he actually stops eating over the next few days.", score: 0, harmful: true, bad: "Allowing it to burst is painful and risks spreading infection." },
          { t: "There's no real rush with this one, so just come in whenever it happens to suit you over the next couple of weeks and we'll take a look at it.", score: 1, bad: "Too slow for an active infection." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A painful cat-bite abscess with a quiet cat needs same-day drainage and antibiotics — urgent, not an emergency.",
      options: [
        { t: "Urgent — today", score: 25, why: "A painful cat-bite abscess with a quiet cat needs same-day drainage and antibiotics." },
        { t: "Emergency — see now", score: 12 },
        { t: "Routine — book in", score: 6 },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "acute-lameness",
    caller: "Mrs Patterson",
    patient: "Bracken",
    signalment: "Springer Spaniel, 6y MN, ~16 kg",
    demeanour: "Concerned",
    summary: "Suddenly non-weight-bearing on a front leg; alert and eating.",
    coachTitle: "Acute non-weight-bearing lameness — dog (urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's come back from his walk and he won't put any weight on his front leg.",
        chips: [{ t: "won't put any weight on his front leg", flag: true, coach: "Sudden non-weight-bearing lameness means significant pain or injury and needs assessment." }],
        options: [
          { t: "Riverside Vet, Sam speaking — let's work out how sore he is. Did he yelp or have a fall, and is he otherwise bright and eating?", score: 3, good: "Gauges severity and screens for systemic illness." },
          { t: "Dogs pull up lame all the time and usually shake it off, so just take him out and gently make him walk on it and it'll soon loosen up again.", score: 0, harmful: true, bad: "Forcing a painful leg can worsen the injury." },
          { t: "Give him one of your own ibuprofen tablets to settle the pain and the swelling down, and then just rest him quietly at home for a few days.", score: 0, harmful: true, bad: "Human NSAIDs like ibuprofen are toxic to dogs." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He yelped jumping off a log. He's bright and ate his dinner, but he's holding the leg up and licking it.",
        chips: [{ t: "holding the leg up", flag: true, coach: "Holding the limb up indicates significant pain; it needs examination for fracture or soft-tissue injury." }],
        options: [
          { t: "Good that he's bright and eating. Keep him rested and lead-only — no jumping or stairs — and let's get the leg checked today.", score: 3, good: "Sensible rest advice plus same-day assessment." },
          { t: "Just while I bring his details up, can you let me know whether he's microchipped, so I can make sure I'm looking at the right patient record?", score: 1, bad: "Not relevant to an acute lameness." },
          { t: "Try massaging the sore leg quite firmly with your hands to work out all the stiffness, and that should help him start putting weight on it again.", score: 0, harmful: true, bad: "Massaging a possible fracture causes pain and harm." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Is this an emergency?",
        chips: [],
        options: [
          { t: "He's stable, so not a middle-of-the-night emergency, but he's in pain — bring him in today for pain relief and an exam, and no human medicines.", score: 3, good: "Proportionate urgency with a clear safety message." },
          { t: "Just give it a few days of strict rest at home and only think about bringing him in if the leg is still no better by the end of the week.", score: 0, harmful: true, bad: "Leaves a painful injury untreated." },
          { t: "Why don't we just add it to the list for his routine check-up next month, and you can rest him gently at home in the meantime to be safe.", score: 1, bad: "Far too slow for an acute painful lameness." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Sudden non-weight-bearing lameness in an otherwise well dog needs same-day pain relief and assessment.",
      options: [
        { t: "Emergency — see now", score: 10 },
        { t: "Urgent — today", score: 25, why: "Sudden non-weight-bearing lameness in an otherwise well dog needs same-day pain relief and assessment." },
        { t: "Routine — book in", score: 6 },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "mild-diarrhoea",
    caller: "Mr Bright",
    patient: "Nala",
    signalment: "Labrador, 3y FN, ~28 kg",
    demeanour: "Relaxed",
    summary: "One day of soft stool; bright, eating and drinking normally.",
    coachTitle: "Mild self-limiting diarrhoea — dog (monitor at home)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's had a bit of loose poo since yesterday, but she's her normal happy self.",
        chips: [
          { t: "loose poo since yesterday", flag: false },
          { t: "normal happy self", flag: false },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — glad she's bright. Is she still eating and drinking normally, and is there any blood or vomiting?", score: 3, good: "Screens for the warning signs that would change the triage." },
          { t: "A bit of loose poo is honestly nothing at all to worry about, so I'd just completely ignore it and she'll be back to her normal self in no time.", score: 1, bad: "Dismissive without first checking for red flags." },
          { t: "The best thing is to starve her completely for two full days to give her gut a proper chance to clear it all out and reset itself again.", score: 1, bad: "Prolonged starvation isn't recommended — short bland feeding is better." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She's eating fine, drinking normally, no blood and no vomiting. She did raid the bin two days ago.",
        chips: [{ t: "raid the bin", flag: false }],
        options: [
          { t: "Sounds like a dietary upset from the bin. Offer a bland diet — boiled chicken and rice — small and often, and keep her well hydrated.", score: 3, good: "Correct cause and sensible home dietary management." },
          { t: "Just while I've got her record open in front of me, can I quickly check whether she's been wormed recently and is up to date with all her worming?", score: 1, bad: "Reasonable, but the picture already fits dietary indiscretion." },
          { t: "You could give her one or two of your own anti-diarrhoea tablets from the cupboard to help firm things up a bit, and that should sort her out quickly.", score: 0, harmful: true, bad: "Human medication shouldn't be given without veterinary advice." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Do I need to bring her in?",
        chips: [],
        options: [
          { t: "As she's bright, eating and drinking with no blood, you can monitor her at home on a bland diet. If it lasts beyond 48 hours, or she becomes flat or starts vomiting, book her in.", score: 3, good: "Appropriate home monitoring with clear safety-netting." },
          { t: "Honestly there's no need to ever come in for this sort of thing — a bit of diarrhoea always sorts itself out on its own within a day or two.", score: 0, harmful: true, bad: "Removes safety-netting — some diarrhoea is serious." },
          { t: "Honestly, I'd bring her in as an emergency right now just to be on the safe side, so the vet can take an urgent look at her without any delay at all.", score: 1, bad: "Over-triage for a bright, well dog with mild signs." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A bright, eating dog with mild self-limiting diarrhoea can be monitored at home with safety-netting.",
      options: [
        { t: "Emergency — see now", score: 0 },
        { t: "Urgent — today", score: 10 },
        { t: "Routine — book in", score: 14 },
        { t: "Advice & monitor at home", score: 25, why: "A bright, eating dog with mild self-limiting diarrhoea can be safely monitored at home with safety-netting." },
      ],
    },
  },

  {
    id: "dental-tartar",
    caller: "Ms Greer",
    patient: "Bonnie",
    signalment: "Yorkshire Terrier, 8y FN, ~4 kg",
    demeanour: "Chatty",
    summary: "Bad breath and brown teeth; eating fine.",
    coachTitle: "Dental tartar / halitosis — dog (routine)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My little dog's breath is really smelly and her teeth look brown. She's eating okay though.",
        chips: [
          { t: "breath is really smelly", flag: false },
          { t: "teeth look brown", flag: false },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — that sounds like dental tartar, which is common in small breeds. Is she eating normally and any sign of mouth pain or drooling?", score: 3, good: "Correctly frames it as dental disease and screens for pain." },
          { t: "You could try gently scraping some of that brown tartar off her teeth yourself with a fingernail or a cloth at home to tidy them up a little.", score: 0, harmful: true, bad: "Home scraping is ineffective and can injure the gums." },
          { t: "A bit of bad breath is really quite normal in older dogs, isn't it, so there's honestly nothing much at all you need to do about it really.", score: 1, bad: "Halitosis usually reflects dental disease worth assessing." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "She eats fine, maybe chews more on one side. No drooling or blood. She's eight years old.",
        chips: [{ t: "chews more on one side", flag: false }],
        options: [
          { t: "Good — no pain signs. Chewing one side can mean a sore tooth, so a dental check is worthwhile. Is she otherwise well in herself?", score: 3, good: "Picks up a subtle clue and proposes the right next step." },
          { t: "Just out of curiosity, what's her favourite treat or chew at the moment, and roughly how often does she tend to get them through the week?", score: 1, bad: "Not clinically useful here." },
          { t: "You could try giving her a little of your own mouthwash on her gums to freshen her breath up and take that nasty smell away for a while.", score: 0, harmful: true, bad: "Human mouthwash can be toxic if swallowed." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I worry or just book her in?",
        chips: [],
        options: [
          { t: "Nothing urgent to worry about — let's book a dental check so the vet can assess whether she needs a scale and polish. Dental chews can help meanwhile.", score: 3, good: "Routine booking with helpful interim advice." },
          { t: "Honestly, I wouldn't bother doing anything at all about it for now — her teeth will most likely be perfectly fine just left as they are.", score: 0, harmful: true, bad: "Untreated dental disease causes pain and tooth loss." },
          { t: "I really think you should bring her in today as an emergency, so the vet can take an urgent look at those teeth before the problem gets any worse.", score: 1, bad: "Over-triage for stable dental disease." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Dental tartar and halitosis in a comfortable, eating dog is a routine dental booking.",
      options: [
        { t: "Emergency — see now", score: 0 },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 25, why: "Dental tartar and halitosis in a comfortable, eating dog is a routine dental booking." },
        { t: "Advice & monitor at home", score: 12 },
      ],
    },
  },

  {
    id: "antifreeze-cat",
    caller: "Mr Hale",
    patient: "Smudge",
    signalment: "DSH, 3y MN, ~4.5 kg",
    demeanour: "Casual — doesn't grasp the danger",
    summary: "Cat licked spilt antifreeze in the garage about half an hour ago.",
    coachTitle: "Ethylene glycol (antifreeze) ingestion — cat (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My cat was in the garage and I think he licked up some of the antifreeze I spilt earlier.",
        chips: [
          { t: "licked up some of the antifreeze", flag: true, coach: "Ethylene glycol (antifreeze) is intensely toxic to cats and causes fatal kidney failure — even a tiny amount is an emergency." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — antifreeze is extremely dangerous for cats, so I'm glad you rang straight away. How long ago was it, and how much do you think he had?", score: 3, good: "Recognises a lethal toxin immediately and gathers the time and dose that decide management." },
          { t: "A quick little lick of that really won't do him much harm at all, so I'd just keep a casual eye on him at home for now and see how he goes.", score: 0, harmful: true, bad: "Dangerously wrong — even a small lick of antifreeze can be fatal to a cat." },
          { t: "Give him a saucer of milk to help settle his stomach and dilute it all down, and then just watch him for any sickness over the next little while.", score: 1, bad: "Home remedies waste the narrow window in which the antidote works." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Maybe half an hour ago. He already seems a bit wobbly and he's drinking a lot.",
        chips: [
          { t: "a bit wobbly", flag: true, coach: "Early ethylene glycol toxicity looks 'drunk' — ataxia within an hour of ingestion." },
          { t: "drinking a lot", flag: true, coach: "Increased thirst is an early sign as the toxin begins to damage the kidneys." },
        ],
        options: [
          { t: "Wobbliness this soon is very concerning with antifreeze — don't wait. Is he still able to walk, and do you have the antifreeze container?", score: 3, good: "Escalates correctly and secures the container to confirm the toxin." },
          { t: "Just so I can complete his notes properly, can I quickly check whether he's had his worming and flea treatment done recently and is all up to date?", score: 1, bad: "Irrelevant to an acute poisoning." },
          { t: "Just let him sleep it off quietly somewhere comfortable for now, and you can always ring us back again tomorrow if he's still seeming unsteady.", score: 0, harmful: true, bad: "Waiting is fatal — kidney injury becomes irreversible within hours." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "He's still up and about, just unsteady. What should I do?",
        chips: [],
        options: [
          { t: "Bring him in immediately — there's an antidote but it only works if we start very early. Bring the antifreeze container so we can confirm it.", score: 3, good: "Correct, urgent: the antidote is time-critical and the container confirms the toxin." },
          { t: "Try to make him vomit at home first by giving him a little salt on the back of his tongue, and that should bring most of it straight back up again.", score: 0, harmful: true, bad: "Salt is dangerous and home decontamination delays the antidote." },
          { t: "Why don't you keep a close eye on him overnight and book him in for first thing in the morning if he's still no better by then.", score: 1, bad: "Far too slow for a rapidly nephrotoxic poison." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Antifreeze ingestion in a cat with early ataxia is a time-critical nephrotoxic emergency — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Ethylene glycol is rapidly fatal in cats; the antidote must be started as early as possible." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "tick-paralysis",
    caller: "Ms Whitaker",
    patient: "Ruby",
    signalment: "Cocker Spaniel, 5y FN, ~14 kg",
    demeanour: "Worried",
    summary: "Wobbly back legs and a changed bark after bushwalking; a tick found behind the ear.",
    coachTitle: "Tick paralysis — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's gone wobbly on her back legs and her bark sounds different. We were bushwalking yesterday.",
        chips: [
          { t: "wobbly on her back legs", flag: true, coach: "Ascending weakness after time in bushland is the classic picture of tick paralysis." },
          { t: "bark sounds different", flag: true, coach: "A change in voice means the paralysis is affecting the throat — the airway is at risk." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — that combination after a bushwalk makes me think of a paralysis tick. Have you found a tick anywhere on her?", score: 3, good: "Links the history to tick paralysis and hunts for the tick straight away." },
          { t: "She's most likely just a bit stiff and sore from such a long walk yesterday, so I'd give her plenty of rest at home and she'll loosen up again.", score: 0, harmful: true, bad: "Dismisses progressive, potentially fatal paralysis." },
          { t: "Try giving her a nice warm bath to help loosen up all those tired, stiff muscles, and that should get her moving about more comfortably again.", score: 1, bad: "Unhelpful and delays urgent assessment." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "Yes — I just found a fat grey tick behind her ear. She's also retched a couple of times and seems breathless.",
        chips: [
          { t: "fat grey tick", flag: true, coach: "A paralysis tick must be removed and tick antiserum given urgently." },
          { t: "breathless", flag: true, coach: "Laboured breathing in tick paralysis is an emergency — the respiratory muscles are failing." },
        ],
        options: [
          { t: "Breathlessness with tick paralysis is an emergency. Keep her calm and cool, and don't give food or water — bring her straight in.", score: 3, good: "Recognises respiratory compromise and gives safe transport advice." },
          { t: "Just pull that tick straight off her yourself, and then keep a close eye on her over the course of the day to see whether she gradually improves.", score: 0, harmful: true, bad: "Removing the tick alone won't reverse circulating toxin — waiting is dangerous." },
          { t: "Just so I can confirm I've got the right patient up on the system, can you tell me whether she's microchipped and registered to you here?", score: 1, bad: "Not the priority during a respiratory emergency." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I give her some food and water before the drive in?",
        chips: [],
        options: [
          { t: "No food or water — her swallowing is affected and she could inhale it. Keep her quiet and cool and come now; we'll have tick antiserum ready.", score: 3, good: "Prevents aspiration and primes the team for antiserum." },
          { t: "Yes, definitely try to get some fluids and a little food into her before you set off, just to help keep her strength up for the journey in.", score: 0, harmful: true, bad: "A dog with throat paralysis can inhale food or water — this risks aspiration." },
          { t: "Why don't you take the tick off and then just wait and see whether removing it fixes the problem on its own before coming all the way in to us.", score: 1, bad: "Signs can worsen for a day or two even after tick removal." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "Tick paralysis with breathing difficulty is an immediate, life-threatening emergency.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Ascending paralysis affecting breathing and swallowing needs antiserum and support now." },
        { t: "Urgent — today", score: 6 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "eye-ulcer-cat",
    caller: "Mrs Okoro",
    patient: "Misty",
    signalment: "Persian, 4y FN, ~3.8 kg",
    demeanour: "Concerned",
    summary: "Holding one eye shut, watering and rubbing it since this morning.",
    coachTitle: "Painful eye / suspected corneal ulcer — cat (urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My cat's holding one eye shut and it's watering a lot — she keeps rubbing at it.",
        chips: [
          { t: "holding one eye shut", flag: true, coach: "A painful, squinting eye suggests a corneal ulcer or injury and needs prompt assessment." },
          { t: "keeps rubbing at it", flag: true, coach: "Rubbing can rapidly worsen a corneal ulcer and even risk rupture." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — a painful eye needs checking today. Is the eye cloudy or is there discharge, and can she open it at all?", score: 3, good: "Characterises the eye and screens for an ulcer while booking same-day care." },
          { t: "Cats do tend to squint and half-close their eyes when they're sleepy or relaxed, so I'd just leave it alone for now and see how it looks later on.", score: 0, harmful: true, bad: "A painful, watering eye is not normal squinting and can deteriorate fast." },
          { t: "Try bathing the eye gently with a little cooled tea on some cotton wool, and then just keep an eye on it to see whether the watering settles down.", score: 1, bad: "Home remedies delay proper assessment of a possible ulcer." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "It looks a bit cloudy in the middle and she won't open it in the light. It started after she was play-fighting this morning.",
        chips: [
          { t: "cloudy in the middle", flag: true, coach: "A cloudy spot on the cornea suggests an ulcer — flat-faced breeds like Persians are especially prone." },
        ],
        options: [
          { t: "That cloudiness could be an ulcer. Stop her rubbing it — a buster collar helps — and let's get her seen today.", score: 3, good: "Protects the eye from self-trauma and arranges timely care." },
          { t: "Just while I've got her record open, can I quickly check whether she's had all of her booster vaccinations and is up to date with everything?", score: 1, bad: "Not relevant to an acute eye problem." },
          { t: "Pop a few of your own eye drops in to soothe and lubricate it for her, and that should take the soreness away while you keep an eye on it.", score: 0, harmful: true, bad: "Some human drops (e.g. steroids) can make a corneal ulcer much worse." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Can it wait until the weekend?",
        chips: [],
        options: [
          { t: "Best not to wait — eye problems can worsen quickly. Keep her from rubbing it and bring her in today; don't put any drops in until we've examined it.", score: 3, good: "Appropriate same-day urgency with safe interim advice." },
          { t: "Yes, eye problems like this usually settle down on their own given a bit of time, so I'd just leave it for a few days and see how she gets on.", score: 0, harmful: true, bad: "An ulcer left for days can deepen and threaten the eye." },
          { t: "Why not try giving her an antihistamine tablet in case the whole thing is just down to an allergy of some sort, and see if that calms it down.", score: 1, bad: "Guesswork medication delays the examination she needs." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A painful, cloudy eye in a cat needs same-day veterinary care before it worsens.",
      options: [
        { t: "Urgent — today", score: 25, why: "A suspected corneal ulcer is painful and can deteriorate fast — it needs same-day assessment." },
        { t: "Emergency — see now", score: 14 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 6 },
      ],
    },
  },

  {
    id: "string-cat",
    caller: "Mr Beaumont",
    patient: "Oscar",
    signalment: "DSH, 1y MN, ~4 kg",
    demeanour: "Worried",
    summary: "Kitten swallowed sewing thread; string seen at the mouth, now vomiting.",
    coachTitle: "Linear foreign body (string) — cat (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My kitten was playing with my sewing thread and I think he's swallowed some — there was string hanging out of his mouth.",
        chips: [
          { t: "swallowed some", flag: true, coach: "Swallowed thread (a linear foreign body) can saw through the intestine — a surgical emergency." },
          { t: "string hanging out of his mouth", flag: true, coach: "Visible string must NEVER be pulled — it can lacerate the gut." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — please don't pull on the string. Is any still hanging from his mouth now, and has he been sick?", score: 3, good: "Gives the critical safety instruction (don't pull) and gathers key signs." },
          { t: "If you can still see the thread there, just take hold of it and very gently pull it back out of his mouth, and that should sort the whole thing out.", score: 0, harmful: true, bad: "Pulling linear foreign bodies can saw through the intestine — never do this." },
          { t: "He'll most likely just pass the thread through naturally in his own time, so keep an eye on his litter tray over the next day or two and check it comes out.", score: 1, bad: "Watchful waiting risks a perforation that needs urgent surgery." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's vomited twice and there's still a bit of thread I can see under his tongue. He's hunched up and quiet.",
        chips: [
          { t: "hunched up and quiet", flag: true, coach: "A hunched, quiet cat is showing abdominal pain — consistent with a linear foreign body." },
        ],
        options: [
          { t: "Leave any thread exactly where it is — don't cut or pull it. He needs to be seen now; bring him straight in.", score: 3, good: "Keeps the thread intact for safe surgical removal and escalates." },
          { t: "Quickly cut off the bit of thread you can see hanging there to stop him swallowing down any more of it, and that should limit how much goes in.", score: 0, harmful: true, bad: "Cutting it lets the rest migrate and bunch the gut — dangerous." },
          { t: "Just so I can make a proper note of it for the vet, roughly how long and what colour was the thread that he managed to swallow down?", score: 1, bad: "Irrelevant detail while he's painful and vomiting." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I feed him to help it move through?",
        chips: [],
        options: [
          { t: "No food or water — he may need surgery. Keep him calm in a carrier and come in now.", score: 3, good: "Correct: keep nil by mouth ahead of likely surgery." },
          { t: "Give him a nice big meal now so that all the food helps push the string along and out through his system the natural way.", score: 0, harmful: true, bad: "Feeding a possible obstruction is unsafe and delays surgery." },
          { t: "Why don't you just wait and keep a close eye on his litter tray to see whether he manages to pass it on his own by tomorrow before coming in.", score: 1, bad: "A linear foreign body rarely passes safely — waiting risks his life." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A swallowed string with vomiting and abdominal pain is a surgical emergency — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Linear foreign bodies can perforate the intestine and need urgent surgical assessment." },
        { t: "Urgent — today", score: 8 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "ear-infection-dog",
    caller: "Mrs Nash",
    patient: "Buddy",
    signalment: "Cocker Spaniel, 6y MN, ~16 kg",
    demeanour: "Relaxed",
    summary: "Shaking head and scratching one ear for a few days; bright and eating well.",
    coachTitle: "Otitis externa / ear infection — dog (routine)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Buddy keeps shaking his head and scratching at one ear. He's fine otherwise, just a bit annoyed by it.",
        chips: [
          { t: "shaking his head", flag: false },
          { t: "fine otherwise", flag: false },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — sounds like that ear's bothering him. Is there any smell, discharge or redness when you look in?", score: 3, good: "Characterises the ear and screens for infection without over-reacting." },
          { t: "All spaniels get a bit of ear trouble now and then with those lovely long floppy ears, don't they, so I honestly wouldn't go worrying about it.", score: 0, harmful: true, bad: "Dismissive — an untreated ear infection is painful and worsens." },
          { t: "Try pouring a little warm olive oil down into the ear to soften up all that wax and gunk, and that should help ease the irritation for him.", score: 1, bad: "Premature home treatment before the ear is examined." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "There's a bit of brown gunk and it smells a little yeasty. He's still eating well and bouncing around.",
        chips: [
          { t: "brown gunk", flag: false },
          { t: "smells a little yeasty", flag: false },
        ],
        options: [
          { t: "That sounds like an ear infection — uncomfortable but not an emergency. Has he been swimming, or could there be a grass seed in there?", score: 3, good: "Names the likely cause and explores triggers like water or grass seeds." },
          { t: "Alongside all the head-shaking, have you noticed him vomiting at all, or going a bit quiet and off his food in himself over the last few days?", score: 2, bad: "A reasonable wellness check, though less targeted to an ear complaint." },
          { t: "Try cleaning it out yourself with a cotton bud pushed right down into the ear canal to get all that gunk out, and that should clear it up nicely.", score: 0, harmful: true, bad: "Cotton buds can pack debris deeper and damage the ear drum." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Do I need to rush him in?",
        chips: [],
        options: [
          { t: "No need to rush — he's bright and eating. Let's book him in over the next day or two; avoid poking anything down the ear and keep him out of water until then.", score: 3, good: "Appropriate routine triage with sensible interim advice." },
          { t: "I'd bring him in as an emergency right now just to be completely safe, so the vet can take an urgent look at him straight away rather than waiting around.", score: 0, bad: "Over-triage — ties up emergency capacity for a stable dog." },
          { t: "Honestly I'd just leave it well alone for now — these ear things nearly always sort themselves out on their own once he stops scratching at it.", score: 0, harmful: true, bad: "Ear infections rarely self-resolve and become more painful if ignored." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "An ear infection in a bright, comfortable, eating dog is a routine booking — not an emergency, but it does need treating.",
      options: [
        { t: "Routine — book in", score: 25, why: "A comfortable, systemically well dog with an ear infection is a routine appointment." },
        { t: "Urgent — today", score: 12 },
        { t: "Advice & monitor at home", score: 14 },
        { t: "Emergency — see now", score: 0 },
      ],
    },
  },

  {
    id: "broken-nail-dog",
    caller: "Mr Donnelly",
    patient: "Max",
    signalment: "Border Terrier, 5y MN, ~9 kg",
    demeanour: "Mildly anxious",
    summary: "Caught a nail running; small bleed now stopped; weight-bearing and happy.",
    coachTitle: "Cracked toenail, bleeding stopped — dog (advice & monitor)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Max caught his nail running in the park and it bled a bit. It's stopped now but I panicked.",
        chips: [
          { t: "bled a bit", flag: false },
          { t: "stopped now", flag: false },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — easily done, and good that it's stopped. Is he putting weight on the paw and otherwise himself?", score: 3, good: "Calm, proportionate response that checks the key sign — weight-bearing." },
          { t: "Any amount of bleeding like that means he could need a blood transfusion, so you really ought to rush him straight in to us as an emergency!", score: 0, bad: "Alarmist over-triage for a minor, self-limiting bleed." },
          { t: "Wrap a nice tight bandage all the way up the whole leg to stop any more bleeding and keep that nail nicely protected for him until it heals.", score: 1, bad: "An over-tight full-leg bandage can cut off circulation — not needed here." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "He's walking fine and licking it a little. The nail's cracked but not hanging off, and there's no more blood.",
        chips: [
          { t: "walking fine", flag: false },
          { t: "no more blood", flag: false },
        ],
        options: [
          { t: "Since it's stopped and he's comfortable, you can manage this at home for now. Keep him from licking it and watch for swelling or fresh bleeding.", score: 3, good: "Correctly judges this as home-manageable with clear monitoring advice." },
          { t: "Just take a pair of pliers and pull that cracked nail off yourself at home, and that'll stop it catching on anything and starting to bleed again.", score: 0, harmful: true, bad: "Removing a nail at home is very painful and risks infection — leave it to the vet if needed." },
          { t: "Soak the whole paw in a bowl of strong undiluted antiseptic for a good while to make sure that little crack is properly cleaned right out.", score: 1, bad: "Neat antiseptic can irritate the tissue — dilute or simply keep it clean." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "So I don't need to come in?",
        chips: [],
        options: [
          { t: "Not right now — keep the paw clean and dry, stop him licking, and bring him in only if it bleeds again, swells, smells, or he goes lame. We're always happy to check it if you're unsure.", score: 3, good: "Sound advice with explicit safety-netting and an open door." },
          { t: "No, you can just ignore it completely — a cracked nail is really nothing at all to worry about and it'll grow back out on its own in no time.", score: 1, bad: "Dismissive and misses the safety-net about infection or lameness." },
          { t: "You could give him a little of your own ibuprofen to take the edge off any pain from the nail, and then just keep him quiet and rested at home.", score: 0, harmful: true, bad: "Ibuprofen is toxic to dogs — never advise human painkillers." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A minor cracked nail that's stopped bleeding in a comfortable, weight-bearing dog can be monitored at home with safety-netting.",
      options: [
        { t: "Advice & monitor at home", score: 25, why: "A self-limiting nail bleed in a comfortable, weight-bearing dog is safely managed at home with clear advice." },
        { t: "Routine — book in", score: 12 },
        { t: "Urgent — today", score: 6 },
        { t: "Emergency — see now", score: 0 },
      ],
    },
  },

  {
    id: "diabetic-collapse",
    caller: "Mrs Iqbal",
    patient: "Honey",
    signalment: "Beagle, 9y FN, ~13 kg, diabetic",
    demeanour: "Frightened",
    summary: "Diabetic dog had insulin but didn't eat; now wobbly and disoriented.",
    coachTitle: "Hypoglycaemia in a diabetic — dog (emergency)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "My dog's diabetic and she's gone really wobbly and confused. I gave her insulin this morning but she didn't eat her breakfast.",
        chips: [
          { t: "insulin this morning but she didn't eat", flag: true, coach: "Insulin without food can cause dangerously low blood sugar — a medical emergency." },
          { t: "wobbly and confused", flag: true, coach: "Wobbliness and confusion in a diabetic strongly suggest hypoglycaemia — act immediately." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — insulin without food can drop her blood sugar dangerously. If she can still swallow, rub a little honey or syrup on her gums right now.", score: 3, good: "Identifies hypoglycaemia and gives life-saving first aid immediately." },
          { t: "She'll most likely be absolutely fine again once the insulin wears off in a few hours, so I'd just let her rest somewhere quiet and comfortable.", score: 0, harmful: true, bad: "Untreated hypoglycaemia can cause seizures and death — never wait it out." },
          { t: "Why don't you give her a second small dose of her insulin now to help balance things back out again, and that should soon bring her round.", score: 0, harmful: true, bad: "More insulin would deepen the hypoglycaemia and could be fatal." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "I've rubbed honey on her gums. She's a bit brighter but still not right — she's been diabetic for two years.",
        chips: [
          { t: "still not right", flag: true, coach: "Even with some improvement, a hypoglycaemic diabetic needs veterinary assessment and monitoring." },
        ],
        options: [
          { t: "Good — that honey helps. She still needs to be seen because her sugar can drop again. Bring her in now, and bring her insulin so we know the dose and timing.", score: 3, good: "Recognises the risk of rebound and gathers the insulin details." },
          { t: "If that honey has perked her up a bit, then you're probably fine to just stay home and simply skip tonight's dose of insulin to be on the safe side.", score: 0, harmful: true, bad: "Self-managing an unstable diabetic at home risks another dangerous crash." },
          { t: "Just while I bring her record up on screen, can I quickly check whether she's had her annual booster vaccination and is up to date with everything?", score: 1, bad: "Not relevant while she's hypoglycaemic." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Should I give her dinner early to keep her sugar up?",
        chips: [],
        options: [
          { t: "If she's alert enough to swallow safely, a small amount of her normal food can help on the way in — but come in now so we can check her glucose and adjust her treatment.", score: 3, good: "Balances safe interim feeding with the need for urgent assessment." },
          { t: "Don't give her anything at all to eat until tomorrow, so that her blood sugar has a proper chance to settle right back down again overnight.", score: 0, harmful: true, bad: "Withholding food from a hypoglycaemic dog is dangerous." },
          { t: "Just keep rubbing a little more honey onto her gums every hour or so at home, and that should be plenty to keep her sugar levels nicely topped up.", score: 1, bad: "Home honey alone won't stabilise an unstable diabetic — she needs to be seen." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A wobbly, confused diabetic after insulin without food is a hypoglycaemic emergency — see now.",
      options: [
        { t: "Emergency — see now", score: 25, why: "Hypoglycaemia in a diabetic can progress to seizures and death — urgent glucose support and monitoring are needed." },
        { t: "Urgent — today", score: 10 },
        { t: "Routine — book in", score: 0, harmful: true },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },

  {
    id: "laceration-dog",
    caller: "Mr Vella",
    patient: "Bruno",
    signalment: "Boxer, 4y MN, ~30 kg",
    demeanour: "Worried",
    summary: "Cut his side jumping a fence; bleeding controlled, wound gaping, bright.",
    coachTitle: "Skin laceration needing repair — dog (urgent)",
    beats: [
      {
        category: "COMMUNICATION",
        prompt: "Pick your opening response.",
        caller: "Bruno cut himself jumping the fence — there's a gash on his side. It's bled but I've got a towel pressed on it.",
        chips: [
          { t: "gash on his side", flag: true, coach: "A gaping laceration usually needs cleaning and stitching within hours for good healing and to avoid infection." },
        ],
        options: [
          { t: "Riverside Vet, Sam speaking — keep that gentle pressure on. Is the bleeding slowing, and roughly how long and deep is the cut?", score: 3, good: "Supports first aid and sizes the wound to judge urgency." },
          { t: "Cuts like that generally heal up perfectly well on their own, so I'd just leave it open to the air and let it scab over and mend by itself.", score: 0, harmful: true, bad: "A gaping wound left open risks infection and poor healing." },
          { t: "Pour some neat, undiluted antiseptic straight into the open wound to give it a really good clean out and kill off any infection before it sets in.", score: 1, bad: "Concentrated antiseptic damages tissue — clean water is safer until he's seen." },
        ],
      },
      {
        category: "HISTORY",
        prompt: "Pick your next question.",
        caller: "The bleeding's nearly stopped with pressure. The cut's about five centimetres and I can see it's gaping open. He's bright and standing.",
        chips: [
          { t: "gaping open", flag: true, coach: "Wound edges that won't sit together need suturing, and fresh wounds close best within hours." },
        ],
        options: [
          { t: "That'll likely need stitching, and sooner is better for clean healing. Keep the towel on, don't put creams or powders in it, and let's get him seen today.", score: 3, good: "Correctly judges same-day repair and gives safe interim care." },
          { t: "Just while I've got his record open in front of me, can I quickly check whether he's up to date with his worming and his vaccinations as well?", score: 1, bad: "Not relevant to an acute wound." },
          { t: "You could just glue the edges of the cut shut yourself at home with a little household superglue, and that'll save you a trip all the way in to us.", score: 0, harmful: true, bad: "Sealing in bacteria with household glue risks a serious wound infection." },
        ],
      },
      {
        category: "ADVICE",
        prompt: "Pick the advice to give.",
        caller: "Can I just clean it and bandage it at home?",
        chips: [],
        options: [
          { t: "Cover it with a clean, dry cloth to protect it, but it really needs professional cleaning and closure today to prevent infection — bring him in this afternoon.", score: 3, good: "Appropriate same-day urgency with safe holding measures." },
          { t: "If you've got a steady hand, you could stitch the wound up yourself at home with a clean needle and thread to close it over nicely for him.", score: 0, harmful: true, bad: "Home suturing is painful, unsterile and traps infection — never advise it." },
          { t: "Just bandage it up nice and tightly to hold the edges together, and then check it again sometime next week to see how well it's healing up.", score: 1, bad: "A week's delay lets the wound become infected and harder to close." },
        ],
      },
    ],
    triage: {
      prompt: "Set the triage category.",
      improve: "A clean but gaping laceration in a stable dog needs same-day cleaning and closure — urgent, not a 'see now' resuscitation nor a routine wait.",
      options: [
        { t: "Urgent — today", score: 25, why: "A gaping wound heals best closed within hours, so it needs same-day veterinary repair." },
        { t: "Emergency — see now", score: 14 },
        { t: "Routine — book in", score: 6 },
        { t: "Advice & monitor at home", score: 0, harmful: true },
      ],
    },
  },
];

// Fisher–Yates shuffle of a copy — leaves the source array untouched.
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Return a fresh copy of a scenario with the selectable options (every beat +
// the final triage) shuffled, so the correct answer never sits in a fixed slot.
// Grading is index-based against THIS returned copy, so order stays consistent.
export const shuffleScenarioOptions = (s) => ({
  ...s,
  beats: s.beats.map((b) => ({ ...b, options: shuffleArray(b.options) })),
  triage: { ...s.triage, options: shuffleArray(s.triage.options) },
});

const flagsOf = (s) => {
  const out = [];
  s.beats.forEach((b) => (b.chips || []).forEach((c) => c.flag && out.push(c)));
  return out;
};

export const totalFlags = (s) => flagsOf(s).length;

const VERDICTS = {
  good: { label: "Competent", tone: "good" },
  warn: { label: "Needs revision", tone: "warn" },
  bad: { label: "Not yet competent", tone: "bad" },
};

const cap = (arr, n) => arr.slice(0, n);

// Compute the Cert IV rubric grade from the player's call.
export function gradeCall(scenario, { picks, triagePick, caught, falseTaps }) {
  const flags = flagsOf(scenario);
  const total = flags.length;
  const caughtSet = new Set(caught);
  const caughtCount = flags.filter((f) => caughtSet.has(f.t)).length;

  // Decision-tree (40)
  let raw = 0;
  let max = 0;
  let harmful = 0;
  scenario.beats.forEach((b, bi) => {
    const best = Math.max(...b.options.map((o) => o.score));
    max += best;
    const opt = b.options[picks[bi]];
    if (opt) {
      raw += opt.score;
      if (opt.harmful) harmful += 1;
    }
  });
  const decisionPts = max ? Math.round((raw / max) * 40) : 40;

  // Red-Flag Radar (25)
  let redFlagPts = total === 0 ? 25 : Math.round((25 * caughtCount) / total);
  redFlagPts = Math.max(0, redFlagPts - falseTaps * 3);

  // Final triage (25)
  const triOpt = scenario.triage.options[triagePick];
  const triagePts = triOpt ? triOpt.score : 0;
  if (triOpt && triOpt.harmful) harmful += 1;

  // Client safety (10)
  const safetyPts = Math.max(0, 10 - harmful * 4);

  const score = Math.min(100, decisionPts + redFlagPts + triagePts + safetyPts);
  const verdict = score >= 85 ? VERDICTS.good : score >= 70 ? VERDICTS.warn : VERDICTS.bad;

  // Coaching
  const strengths = [];
  const improvements = [];
  const idealTriage = scenario.triage.options.find((o) => o.score === 25);

  if (triagePts === 25 && triOpt) strengths.push(triOpt.why || `Correct triage decision: ${triOpt.t}.`);
  else improvements.push(scenario.triage.improve);

  scenario.beats.forEach((b, bi) => {
    const best = Math.max(...b.options.map((o) => o.score));
    const opt = b.options[picks[bi]];
    if (!opt) return;
    if (opt.score === best && opt.good) strengths.push(opt.good);
    else if (opt.score !== best && opt.bad) improvements.push(opt.bad);
  });

  flags.forEach((f) => {
    if (!caughtSet.has(f.t)) improvements.push(f.coach);
  });

  if (total > 0 && caughtCount === total)
    strengths.push("Caught every red flag as it was spoken — strong live clinical listening.");
  if (harmful === 0)
    strengths.push("No unsafe or harmful advice — professional, client-safe communication throughout.");
  if (falseTaps > 0)
    improvements.push(
      `You flagged ${falseTaps} phrase${falseTaps > 1 ? "s" : ""} that ${falseTaps > 1 ? "weren't" : "wasn't"} a red flag — stay precise so you don't over-escalate benign detail.`
    );

  if (strengths.length === 0) strengths.push("Courteous, structured call handling.");
  if (improvements.length === 0)
    improvements.push("Keep verbalising warning signs as you hear them to evidence your clinical reasoning.");

  const summary = `${
    triagePts === 25 ? "You reached the correct triage decision" : "Your final triage decision needs review"
  }${
    total > 0
      ? caughtCount === total
        ? " and caught every red flag"
        : `, but caught only ${caughtCount} of ${total} red flags`
      : ""
  }. ${harmful > 0 ? "Review the unsafe picks flagged below." : "Your communication stayed safe and professional."}`;

  const rubric = [
    {
      label: "Decision-tree responses (history, advice, communication)",
      got: decisionPts,
      max: 40,
      note: `You scored ${raw} of ${max} raw weighted points across your conversational picks.`,
    },
    {
      label: "Red-Flag Radar (live tapping)",
      got: redFlagPts,
      max: 25,
      note: `You caught ${caughtCount} of ${total} red flags. False taps: ${falseTaps}.`,
    },
    {
      label: "Final triage category",
      got: triagePts,
      max: 25,
      note: `You chose ${triOpt ? triOpt.t : "—"}. Ideal: ${idealTriage ? idealTriage.t : "—"}.`,
    },
    {
      label: "Client safety & professional communication",
      got: safetyPts,
      max: 10,
      note: `Harmful or unsafe selections: ${harmful}. Each harmful pick costs 4 points.`,
    },
  ];

  const revealed = flags.map((f) => ({ phrase: f.t, caught: caughtSet.has(f.t) }));

  return {
    score,
    verdict,
    triageCorrect: triagePts === 25,
    triageChosen: triOpt ? triOpt.t : "—",
    caughtCount,
    totalFlags: total,
    falseTaps,
    title: scenario.coachTitle,
    summary,
    strengths: cap(strengths, 4),
    improvements: cap(improvements, 4),
    rubric,
    revealed,
  };
}
